// API service for handling authentication and product data

const API_BASE_URL = 'https://et-pos.store';
const ROLE_API_BASE_URL = 'https://et-pos.store';

// Token storage keys
const TOKEN_KEY = 'etpos_token';
const TOKEN_EXPIRY_KEY = 'etpos_token_expiry';
const BUSINESS_TIMEZONE_KEY = 'etpos_business_timezone';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.roleBaseURL = ROLE_API_BASE_URL;
  }

  // Login with username and password using OAuth token endpoint
  async login(username, password) {
    try {
      const response = await fetch(`${this.baseURL}/oauth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'password',
          client_id: '32',
          client_secret: 'MjSuv4nquj67caVWanVpDX5YjrTdiXBVuAooJ13r',
          username: username,
          password: password
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        /* console.error('Login failed:', errorText); */

        if (response.status === 401) {
          throw new Error('Incorrect Username, Password, Or user not active.');
        } else if (response.status === 500) {
          throw new Error('Server error. Please contact administrator.');
        } else {
          throw new Error('Incorrect Username, Password, Or user not active.');
        }
      }

      const data = await response.json();

      if (data.access_token) {
        // Clear previous user cached settings and product lists on new login
        this.clearCachedBusinessSettings();

        // Store token with expiry
        const expiryTime = new Date();
        expiryTime.setHours(expiryTime.getHours() + 24); // 24 hour expiry

        localStorage.setItem(TOKEN_KEY, data.access_token);
        localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toISOString());

        // Fetch and cache business timezone and all other settings after successful login
        try {
          // Use user/loggedin which always succeeds regardless of role - it returns business.currency eagerly
          const loggedinRes = await fetch(`${this.baseURL}/connector/api/user/loggedin`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${data.access_token}`, 'Content-Type': 'application/json' }
          });
          if (loggedinRes.ok) {
            const loggedinData = await loggedinRes.json();
            const userData = loggedinData.data || loggedinData;
            const biz = userData?.business || loggedinData?.business;

            if (userData) {
              localStorage.setItem('etpos_user', JSON.stringify(userData));
            }
            if (biz) {
              this.cacheBusinessSettingsFromData(biz);
            }
          }
          window.dispatchEvent(new Event('etpos-settings-update'));
        } catch (err) { /* console.error('Error fetching settings on login:', err); */ }

        return { success: true, token: data.access_token };
      } else {
        throw new Error('No access token received from server');
      }
    } catch (error) {
      /* console.error('Error during login:', error); */
      throw error;
    }
  }

  // Clear all business-specific cached settings to prevent leakage between businesses
  clearCachedBusinessSettings() {
    const keysToRemove = [
      'etpos_currency_symbol',
      'etpos_currency_placement',
      'etpos_currency_precision',
      'etpos_decimal_separator',
      'etpos_thousand_separator',
      'etpos_date_format',
      'etpos_time_format',
      'etpos_business_timezone',
      'etpos_quantity_precision',
      'etpos_default_profit_percent',
      'etpos_enable_brand',
      'etpos_enable_category',
      'etpos_enable_sub_category',
      'etpos_enable_price_tax',
      'etpos_enable_sub_units',
      'etpos_enable_secondary_unit',
      'etpos_enable_product_warranty',
      'etpos_is_product_image_required',
      'etpos_enable_product_image_in_list',
      'etpos_enable_racks',
      'etpos_enable_row',
      'etpos_enable_position',
      'etpos_enable_product_expiry',
      'etpos_sku_prefix',
      'etpos_default_unit',
      'etpos_sales_cmsn_agnt',
      'etpos_is_pay_term_required',
      'etpos_is_commission_agent_required',
      'etpos_allow_overselling',
      'etpos_enable_msp',
      'etpos_default_sales_discount',
      'etpos_default_sales_tax',
      'etpos_enable_sales_order',
      'etpos_enable_editing_product_from_purchase',
      'etpos_enable_purchase_status',
      'etpos_enable_lot_number',
      'etpos_enable_purchase_order',
      'etpos_enable_purchase_requisition',
      'etpos_business_name',
      'etpos_business_logo',
      'etpos_user',
      'etpos_cached_products_list',
      'etpos_pos_settings',
      'etpos_pos_keyboard_shortcuts',
      'etpos_user_permissions',
    ];
    keysToRemove.forEach(key => localStorage.removeItem(key));
  }

  // Cache target business settings from API response cleanly
  cacheBusinessSettingsFromData(biz) {
    if (!biz) return;

    if (biz.name) localStorage.setItem('etpos_business_name', biz.name);
    else localStorage.removeItem('etpos_business_name');

    if (biz.logo) localStorage.setItem('etpos_business_logo', biz.logo);
    else localStorage.removeItem('etpos_business_logo');

    if (biz.time_zone) localStorage.setItem(BUSINESS_TIMEZONE_KEY, biz.time_zone);
    if (biz.date_format) localStorage.setItem('etpos_date_format', biz.date_format);
    if (biz.time_format) localStorage.setItem('etpos_time_format', String(biz.time_format));
    if (biz.currency_symbol_placement) localStorage.setItem('etpos_currency_placement', biz.currency_symbol_placement);

    if (biz.currency_precision !== undefined && biz.currency_precision !== null) {
      localStorage.setItem('etpos_currency_precision', String(biz.currency_precision));
    }
    if (biz.quantity_precision !== undefined && biz.quantity_precision !== null) {
      localStorage.setItem('etpos_quantity_precision', String(biz.quantity_precision));
    }
    if (biz.default_profit_percent !== undefined && biz.default_profit_percent !== null) {
      localStorage.setItem('etpos_default_profit_percent', String(biz.default_profit_percent));
    }

    const currencySymbol = biz.currency?.symbol || biz.currency_symbol;
    if (currencySymbol) localStorage.setItem('etpos_currency_symbol', currencySymbol);

    const decimalSep = biz.currency?.decimal_separator || biz.decimal_separator;
    if (decimalSep) localStorage.setItem('etpos_decimal_separator', decimalSep);

    const thousandSep = biz.currency?.thousand_separator || biz.thousand_separator;
    if (thousandSep) localStorage.setItem('etpos_thousand_separator', thousandSep);

    if (biz.enable_brand !== undefined) localStorage.setItem('etpos_enable_brand', String(biz.enable_brand));
    if (biz.enable_category !== undefined) localStorage.setItem('etpos_enable_category', String(biz.enable_category));
    if (biz.enable_sub_category !== undefined) localStorage.setItem('etpos_enable_sub_category', String(biz.enable_sub_category));
    if (biz.enable_price_tax !== undefined) localStorage.setItem('etpos_enable_price_tax', String(biz.enable_price_tax));
    if (biz.enable_sub_units !== undefined) localStorage.setItem('etpos_enable_sub_units', String(biz.enable_sub_units));
    if (biz.enable_racks !== undefined) localStorage.setItem('etpos_enable_racks', String(biz.enable_racks));
    if (biz.enable_row !== undefined) localStorage.setItem('etpos_enable_row', String(biz.enable_row));
    if (biz.enable_position !== undefined) localStorage.setItem('etpos_enable_position', String(biz.enable_position));
    if (biz.enable_product_expiry !== undefined) localStorage.setItem('etpos_enable_product_expiry', String(biz.enable_product_expiry));
    if (biz.sku_prefix !== undefined) localStorage.setItem('etpos_sku_prefix', String(biz.sku_prefix || ''));
    if (biz.default_unit !== undefined) localStorage.setItem('etpos_default_unit', String(biz.default_unit || ''));
    if (biz.sales_cmsn_agnt !== undefined) localStorage.setItem('etpos_sales_cmsn_agnt', String(biz.sales_cmsn_agnt || ''));
    if (biz.default_sales_discount !== undefined) localStorage.setItem('etpos_default_sales_discount', String(biz.default_sales_discount || '0.00'));
    if (biz.default_sales_tax !== undefined) localStorage.setItem('etpos_default_sales_tax', String(biz.default_sales_tax || ''));

    if (biz.enable_editing_product_from_purchase !== undefined) {
      localStorage.setItem('etpos_enable_editing_product_from_purchase', String(biz.enable_editing_product_from_purchase));
    }
    if (biz.enable_purchase_status !== undefined) {
      localStorage.setItem('etpos_enable_purchase_status', String(biz.enable_purchase_status));
    }
    if (biz.enable_lot_number !== undefined) {
      localStorage.setItem('etpos_enable_lot_number', String(biz.enable_lot_number));
    }

    const common = biz.common_settings || {};
    if (common.enable_secondary_unit !== undefined) localStorage.setItem('etpos_enable_secondary_unit', String(common.enable_secondary_unit));
    if (common.enable_product_warranty !== undefined) localStorage.setItem('etpos_enable_product_warranty', String(common.enable_product_warranty));
    if (common.is_product_image_required !== undefined) localStorage.setItem('etpos_is_product_image_required', String(common.is_product_image_required));
    if (common.enable_product_image_in_list !== undefined) localStorage.setItem('etpos_enable_product_image_in_list', String(common.enable_product_image_in_list));
    if (common.enable_purchase_order !== undefined) localStorage.setItem('etpos_enable_purchase_order', String(common.enable_purchase_order));
    if (common.enable_purchase_requisition !== undefined) localStorage.setItem('etpos_enable_purchase_requisition', String(common.enable_purchase_requisition));
  }

  // Get authentication token
  async getAuthToken() {
    // Check if we have a valid token stored
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (storedToken && tokenExpiry && new Date(tokenExpiry) > new Date()) {
      return storedToken;
    }

    if (storedToken) {
      // Fallback: If token exists, refresh expiry timestamp for 24h
      const expiryTime = new Date();
      expiryTime.setHours(expiryTime.getHours() + 24);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toISOString());
      return storedToken;
    }

    // If no valid token, user needs to login
    throw new Error('No valid authentication token. Please login.');
  }

  // Helper for authorized API fetch
  async fetchWithAuth(endpoint, options = {}) {
    const token = await this.getAuthToken();
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(options.headers || {})
    };
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
    const response = await fetch(url, { ...options, headers });
    if (!response.ok) {
      throw new Error(`API Error ${response.status}: ${response.statusText}`);
    }
    return await response.json();
  }

  // Download EDI flat file (.txt) via Connector API using Bearer Token
  async downloadEdi(transactionId) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/sells/${transactionId}/download-edi`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/plain, application/json'
        }
      });
      if (!response.ok) {
        throw new Error(`EDI Download failed: ${response.statusText}`);
      }
      const blob = await response.blob();
      const filename = response.headers.get('content-disposition')?.split('filename=')[1]?.replace(/"/g, '') || `EDI_${transactionId}.txt`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      throw err;
    }
  }

  // Get active open cash register for current user
  async getOpenCashRegister() {
    try {
      let userQuery = '';
      try {
        const user = await this.getUserDetails();
        const userId = user?.id || user?.data?.id || '';
        if (userId) userQuery = `&user_id=${userId}`;
      } catch (e) { }

      const res = await this.fetchWithAuth(`/connector/api/cash-register?status=open${userQuery}`);
      let list = res?.data || res || [];
      if (!Array.isArray(list) && list.data && Array.isArray(list.data)) list = list.data;
      if (Array.isArray(list) && list.length > 0) return list[0];

      return null;
    } catch (err) {
      return null;
    }
  }

  // Get cash register transaction details
  async getCashRegisterDetails(registerId) {
    try {
      const regId = registerId || '';
      const endpoint = regId ? `/connector/api/cash-register/${regId}/details` : `/connector/api/cash-register/details`;
      const res = await this.fetchWithAuth(endpoint);
      return res?.data || res;
    } catch (err) {
      return null;
    }
  }

  // Close cash register session
  async closeCashRegister(registerId, data = {}) {
    try {
      const payload = { id: registerId, ...data };
      const res = await this.fetchWithAuth(`/connector/api/cash-register/close`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      return res;
    } catch (err) {
      throw err;
    }
  }

  // Open new cash register session
  async openCashRegister(data = {}) {
    try {
      const payload = { status: 'open', initial_amount: 0, ...data };
      const res = await this.fetchWithAuth(`/connector/api/cash-register`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      return res;
    } catch (err) {
      throw err;
    }
  }

  // Get available payment methods for business location
  async getPaymentMethods(locationId = '') {
    try {
      const query = locationId ? `?location_id=${locationId}` : '';
      const res = await this.fetchWithAuth(`/connector/api/payment-methods${query}`);
      return res?.data || res || {};
    } catch (err) {
      return { cash: 'Cash', card: 'Card', cheque: 'Cheque', bank_transfer: 'Bank Transfer', other: 'Other' };
    }
  }

  // Get user details (business_id, location_id, etc.)
  async getUserDetails() {
    /* console.log('🔐 RBAC DEBUG - Fetching user details...'); */
    try {
      const token = await this.getAuthToken();
      /* console.log('🔐 RBAC DEBUG - Token obtained for user details:', token ? 'success' : 'failed'); */

      if (!token) {
        /* console.error('🔐 RBAC DEBUG - No token available, cannot fetch user details'); */
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${this.baseURL}/connector/api/user/loggedin`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      /* console.log('🔐 RBAC DEBUG - User details response status:', response.status); */
      /* console.log('🔐 RBAC DEBUG - User details response ok:', response.ok); */

      if (!response.ok) {
        const errorText = await response.text();
        /* console.error('🔐 RBAC DEBUG - User details error response:', errorText); */
        throw new Error(`Failed to fetch user details: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      /* console.log('🔐 RBAC DEBUG - User details response data:', data); */

      // Extract actual user data from nested response
      const userData = data.data || data;
      /* console.log('🔐 RBAC DEBUG - Extracted user data:', userData); */
      /* console.log('🔐 RBAC DEBUG - User roles:', userData.roles); */
      /* console.log('🔐 RBAC DEBUG - User all_permissions:', userData.all_permissions); */
      /* console.log('🔐 RBAC DEBUG - User business_id:', userData.business_id); */
      /* console.log('🔐 RBAC DEBUG - User data keys:', Object.keys(userData)); */

      if (userData.all_permissions && Array.isArray(userData.all_permissions)) {
        /* console.log('🔐 RBAC DEBUG - Found direct permissions:', userData.all_permissions); */
        /* console.log('🔐 RBAC DEBUG - Permission count:', userData.all_permissions.length); */
      } else if (userData.roles && userData.roles.length > 0) {
        /* console.log('🔐 RBAC DEBUG - Primary role:', userData.roles[0]); */
        /* console.log('🔐 RBAC DEBUG - Role permissions:', userData.roles[0].permissions); */
      } else {
        /* console.log('🔐 RBAC DEBUG - No roles or all_permissions found in user data'); */
        /* console.log('🔐 RBAC DEBUG - Available user fields:', Object.keys(userData)); */
      }
      /* console.log('🔐 RBAC DEBUG - getUserDetails completed successfully'); */
      return data;
    } catch (error) {
      /* console.error('🔐 RBAC DEBUG - Error fetching user details:', error); */
      /* console.log('🔐 RBAC DEBUG - Error details:', error.message, error.stack); */
      throw error;
    }
  }

  // ==========================================
  // Superadmin Endpoints
  // ==========================================
  async getSuperadminBusinesses() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/business`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch businesses');
      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching businesses:', error); */
      throw error;
    }
  }

  async getSuperadminBusinessCreateData() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/business/create`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch business create data');
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching business create data:', error); */
      throw error;
    }
  }

  async getSuperadminPackages() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/packages`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch packages');
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching packages:', error); */
      throw error;
    }
  }

  async getSuperadminAllSubscriptions() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/all-subscriptions`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch subscriptions');
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching subscriptions:', error); */
      throw error;
    }
  }

  async getSuperadminStats(start, end) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/stats?start=${start}&end=${end}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch stats');
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching stats:', error); */
      throw error;
    }
  }

  async getSuperadminMonthlyChart() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/monthly-chart`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch chart data');
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching chart data:', error); */
      throw error;
    }
  }

  async toggleBusinessActive(businessId, isActive) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/business/${businessId}/toggle-active/${isActive}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to toggle business status');
      return await response.json();
    } catch (error) {
      /* console.error('Error toggling business status:', error); */
      throw error;
    }
  }

  async updateBusinessUserPassword(userId, password) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/business/update-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: userId, password })
      });
      if (!response.ok) throw new Error('Failed to update user password');
      return await response.json();
    } catch (error) {
      /* console.error('Error updating user password:', error); */
      throw error;
    }
  }

  async deleteBusiness(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/business/${id}/destroy`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to delete business');
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting business:', error); */
      throw error;
    }
  }

  async addBusiness(data) {
    try {
      const token = await this.getAuthToken();
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (data[key] !== undefined && data[key] !== null) {
          formData.append(key, data[key]);
        }
      });
      const response = await fetch(`${this.baseURL}/api/superadmin/business`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
          // Note: Do NOT set Content-Type for FormData; browser sets multipart boundary
        },
        body: formData
      });
      if (!response.ok) throw new Error('Failed to add business');
      return await response.json();
    } catch (error) {
      /* console.error('Error adding business:', error); */
      throw error;
    }
  }

  async addPackage(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/packages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to add package');
      return await response.json();
    } catch (error) {
      /* console.error('Error adding package:', error); */
      throw error;
    }
  }

  async updatePackage(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/packages/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to update package');
      return await response.json();
    } catch (error) {
      /* console.error('Error updating package:', error); */
      throw error;
    }
  }

  async deletePackage(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/packages/${id}/destroy`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to delete package');
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting package:', error); */
      throw error;
    }
  }

  async getSuperadminSubscriptionsList(filters = {}) {
    try {
      const token = await this.getAuthToken();
      const params = new URLSearchParams(filters).toString();
      const response = await fetch(`${this.baseURL}/api/superadmin/superadmin-subscription?${params}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch subscriptions');
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching subscriptions list:', error); */
      throw error;
    }
  }

  async addSuperadminSubscription(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/superadmin-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to add subscription');
      return await response.json();
    } catch (error) {
      /* console.error('Error adding subscription:', error); */
      throw error;
    }
  }

  async getSuperadminSubscriptionEdit(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/superadmin-subscription/${id}/edit`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch subscription edit details');
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching subscription edit:', error); */
      throw error;
    }
  }

  async updateSuperadminSubscriptionStatus(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/superadmin-subscription/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to update subscription status');
      return await response.json();
    } catch (error) {
      /* console.error('Error updating subscription status:', error); */
      throw error;
    }
  }

  async getSuperadminSubscriptionDates(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/edit-subscription/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch subscription dates');
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching subscription dates:', error); */
      throw error;
    }
  }

  async updateSuperadminSubscriptionDates(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/update-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to update subscription dates');
      return await response.json();
    } catch (error) {
      /* console.error('Error updating subscription dates:', error); */
      throw error;
    }
  }

  async getCoupons() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/coupons`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch coupons');
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching coupons:', error); */
      throw error;
    }
  }

  async addCoupon(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/coupons`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to add coupon');
      return await response.json();
    } catch (error) {
      /* console.error('Error adding coupon:', error); */
      throw error;
    }
  }

  async getCouponCreateData() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/coupons/create`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch coupon create data');
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching coupon create data:', error); */
      throw error;
    }
  }

  async getCouponEdit(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/coupons/${id}/edit`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch coupon details');
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching coupon edit:', error); */
      throw error;
    }
  }

  async updateCoupon(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/coupons/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to update coupon');
      return await response.json();
    } catch (error) {
      /* console.error('Error updating coupon:', error); */
      throw error;
    }
  }

  async deleteCoupon(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/coupons/${id}/destroy`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to delete coupon');
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting coupon:', error); */
      throw error;
    }
  }

  async getSuperadminSettings() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/settings`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch settings');
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching settings:', error); */
      throw error;
    }
  }

  async updateSuperadminSettings(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to update settings');
      return await response.json();
    } catch (error) {
      /* console.error('Error updating settings:', error); */
      throw error;
    }
  }

  async getCommunicatorData() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/communicator`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch communicator businesses');
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching communicator businesses:', error); */
      throw error;
    }
  }

  async sendCommunicatorNotification(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/communicator/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to send communicator notification');
      return await response.json();
    } catch (error) {
      /* console.error('Error sending communicator notification:', error); */
      throw error;
    }
  }

  async getCommunicatorHistory() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/communicator/get-history`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch communicator history');
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching communicator history:', error); */
      throw error;
    }
  }

  // ==========================================
  // Regular Business Endpoints
  // ==========================================

  async getActiveSubscription() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/active-subscription`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch active subscription');
      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching active subscription:', error); */
      throw error;
    }
  }

  async getPackages() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/packages`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch packages');
      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching packages:', error); */
      throw error;
    }
  }

  async getSubscriptions() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/subscriptions`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch subscriptions history');
      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching subscriptions history:', error); */
      throw error;
    }
  }

  // Get profit/loss report data
  async getProfitLossReport(params = {}) {
    try {
      const token = await this.getAuthToken();

      const queryParams = new URLSearchParams();
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.location_id) queryParams.append('location_id', params.location_id);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${this.baseURL}/connector/api/profit-loss-report${queryString}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch profit/loss report: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching profit/loss report:', error); */
      throw error;
    }
  }

  // Get dashboard totals (sales, purchase, due, expense, net)
  async getDashboardTotals(params = {}) {
    try {
      const token = await this.getAuthToken();

      const queryParams = new URLSearchParams();
      if (params.start) queryParams.append('start', params.start);
      if (params.end) queryParams.append('end', params.end);
      if (params.location_id) queryParams.append('location_id', params.location_id);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${this.baseURL}/connector/api/dashboard-totals${queryString}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch dashboard totals: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching dashboard totals:', error); */
      throw error;
    }
  }

  // Get sales last 30 days data
  async getSalesLast30Days() {
    try {
      const token = await this.getAuthToken();

      const response = await fetch(`${this.baseURL}/api/dashboard/sales-last-30-days`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch sales last 30 days: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching sales last 30 days:', error); */
      throw error;
    }
  }

  async getSalesCurrentFinancialYear(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      if (params.location_id) queryParams.append('location_id', params.location_id);
      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${this.baseURL}/connector/api/dashboard/sales-current-financial-year${queryString}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch financial year sales: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching financial year sales:', error); */ throw error; }
  }

  async getPaymentDueChart(type = 'sell', params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      queryParams.append('type', type);
      if (params.location_id) queryParams.append('location_id', params.location_id);
      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${this.baseURL}/connector/api/dashboard/payment-due-chart${queryString}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch payment due chart: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching payment due chart:', error); */ throw error; }
  }

  async getSalesPaymentDues(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      if (params.location_id) queryParams.append('location_id', params.location_id);
      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${this.baseURL}/connector/api/dashboard/sales-payment-dues${queryString}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch sales payment dues: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching sales payment dues:', error); */ throw error; }
  }

  async getPurchasePaymentDues(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      if (params.location_id) queryParams.append('location_id', params.location_id);
      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${this.baseURL}/connector/api/dashboard/purchase-payment-dues${queryString}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch purchase payment dues: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching purchase payment dues:', error); */ throw error; }
  }

  async getChequePayments(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      if (params.location_id) queryParams.append('location_id', params.location_id);
      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${this.baseURL}/connector/api/dashboard/cheque-payments${queryString}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch cheque payments: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching cheque payments:', error); */ throw error; }
  }

  async getContactPayments(contactId) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/contacts/${contactId}/payments`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch contact payments: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching contact payments:', error); */ throw error; }
  }

  async updateChequeStatus(id, status) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/dashboard/cheque-payments/${id}/status`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (!response.ok) throw new Error(`Failed to update cheque status: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error updating cheque status:', error); */ throw error; }
  }

  async applyCheckReturn(paymentId, transactionId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/payments/${paymentId}/check-return`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || data.msg || `Failed to apply check return: ${response.status}`);
      }
      return data;
    } catch (error) {
      throw error;
    }
  }

  async revertCheckReturn(paymentId, transactionId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/payments/${paymentId}/revert-check-return`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || data.msg || `Failed to revert check return: ${response.status}`);
      }
      return data;
    } catch (error) {
      throw error;
    }
  }

  async payContactDue(payload) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/payment-methods/pay-contact-due`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(`Failed to pay contact due: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error paying contact due:', error); */ throw error; }
  }

  async getProductStockAlert(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      if (params.location_id) queryParams.append('location_id', params.location_id);
      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${this.baseURL}/connector/api/dashboard/product-stock-alert${queryString}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch product stock alert: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching product stock alert:', error); */ throw error; }
  }

  // Generate Product Description AI
  async generateProductDescription(productName) {
    try {
      const token = await this.getAuthToken();
      const formData = new FormData();
      formData.append('product_name', productName);

      const response = await fetch(`${this.baseURL}/connector/api/product/generate-description`, {
        method: 'POST',
        body: formData,
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to generate description: ${response.status}`);
      return await response.json();
    } catch (error) { throw error; }
  }

  // Create a new product — POST /connector/api/product
  async createProduct(productData) {
    try {
      if (!navigator.onLine) {
        const { addToSyncQueue } = require('./db');
        await addToSyncQueue({
          action: 'CREATE_PRODUCT',
          data: productData
        });
        /* console.log('Offline: Product queued for sync'); */
        return { success: true, message: 'Product saved offline. Will sync when internet is restored.' };
      }

      const token = await this.getAuthToken();

      // Initialize session by calling getUserDetails first
      // This ensures the API server has a valid session before creating the product
      try {
        await this.getUserDetails();
        /* console.log('Session initialized successfully'); */
      } catch (sessionError) {
        /* console.warn('Failed to initialize session, continuing anyway:', sessionError); */
      }

      const url = `${this.baseURL}/connector/api/product`;
      /* console.log('Creating product with data:', productData); */

      // Use FormData so we can attach image/brochure files alongside JSON-like fields
      const formData = new FormData();

      // ── Core fields ────────────────────────────────────────────────────────
      const appendIfSet = (key, value) => {
        if (value !== null && value !== undefined && value !== '') {
          formData.append(key, value);
        }
      };

      appendIfSet('name', productData.name);
      appendIfSet('type', productData.type || 'single');
      appendIfSet('sku', productData.sku);
      appendIfSet('barcode_type', productData.barcode_type);
      appendIfSet('unit_id', productData.unit_id);
      appendIfSet('brand_id', productData.brand_id);
      appendIfSet('category_id', productData.category_id);
      appendIfSet('sub_category_id', productData.sub_category_id);
      appendIfSet('tax', productData.tax);
      appendIfSet('tax_type', productData.tax_type);
      appendIfSet('enable_stock', productData.enable_stock);
      appendIfSet('not_for_selling', productData.not_for_selling);
      appendIfSet('alert_quantity', productData.alert_quantity);
      appendIfSet('product_description', productData.product_description);
      appendIfSet('weight', productData.weight);
      appendIfSet('warranty_id', productData.warranty_id);
      appendIfSet('sub_unit_ids', productData.sub_unit_ids);
      appendIfSet('preparation_time_in_minutes', productData.preparation_time_in_minutes);

      // ── Pricing (single product) ────────────────────────────────────────────
      appendIfSet('single_dpp', productData.single_dpp);
      appendIfSet('single_dpp_inc_tax', productData.single_dpp_inc_tax);
      appendIfSet('profit_percent', productData.profit_percent);
      appendIfSet('single_dsp', productData.single_dsp);
      appendIfSet('single_dsp_inc_tax', productData.single_dsp_inc_tax);

      // ── product_locations: array → FormData array notation ────────────────
      if (Array.isArray(productData.product_locations)) {
        productData.product_locations.forEach((locId) => {
          formData.append('product_locations[]', locId);
        });
      }

      // ── product_racks: object → FormData nested notation ──────────────────
      if (productData.product_racks && typeof productData.product_racks === 'object') {
        Object.entries(productData.product_racks).forEach(([locationId, rack]) => {
          formData.append(`product_racks[${locationId}][rack]`, rack.rack || '');
          formData.append(`product_racks[${locationId}][row]`, rack.row || '');
          formData.append(`product_racks[${locationId}][position]`, rack.position || '');
        });
      }

      // ── product_variation: variable product variations ────────────────────
      if (productData.type === 'variable' && Array.isArray(productData.product_variation)) {
        productData.product_variation.forEach((variation, vi) => {
          formData.append(`product_variation[${vi}][name]`, variation.name || '');
          if (Array.isArray(variation.variations)) {
            variation.variations.forEach((v, vvi) => {
              formData.append(`product_variation[${vi}][variations][${vvi}][name]`, v.name || '');
              formData.append(`product_variation[${vi}][variations][${vvi}][default_purchase_price]`, v.default_purchase_price || 0);
              formData.append(`product_variation[${vi}][variations][${vvi}][dpp_inc_tax]`, v.dpp_inc_tax || 0);
              formData.append(`product_variation[${vi}][variations][${vvi}][profit_percent]`, v.profit_percent || 0);
              formData.append(`product_variation[${vi}][variations][${vvi}][default_sell_price]`, v.default_sell_price || 0);
              formData.append(`product_variation[${vi}][variations][${vvi}][sell_price_inc_tax]`, v.sell_price_inc_tax || 0);
            });
          }
        });
      }

      // ── opening_stock: array → FormData nested notation ──────────────────
      if (Array.isArray(productData.opening_stock)) {
        productData.opening_stock.forEach((os, index) => {
          formData.append(`opening_stock[${index}][quantity]`, os.quantity || '');
          formData.append(`opening_stock[${index}][unit_cost]`, os.unit_cost || '');
          formData.append(`opening_stock[${index}][total_amount]`, os.total_amount || '');
          formData.append(`opening_stock[${index}][location_id]`, os.location_id || '');
          formData.append(`opening_stock[${index}][date]`, os.date || '');
          formData.append(`opening_stock[${index}][note]`, os.note || '');
        });
      }

      // ── Files ──────────────────────────────────────────────────────────────
      if (productData.image) formData.append('image', productData.image);
      if (productData.brochure) formData.append('brochure', productData.brochure);

      // Console log request payload details
      /* console.log('Product creation payload productData:', productData); */
      /* console.log('Product creation FormData entries:'); */
      for (let [key, value] of formData.entries()) {
        /* console.log(`${key}:`, value); */
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Do NOT set Content-Type — browser sets it with boundary for FormData
        },
        body: formData
      });
      /* console.log('Product creation response status:', response.status); */

      if (!response.ok) {
        const errorText = await response.text();
        /* console.error('Product creation error:', errorText); */
        let errMsg = `Failed to create product: ${response.status}`;
        try {
          const errJson = JSON.parse(errorText);
          errMsg = errJson.message || errMsg;
        } catch (_) { /* not JSON */ }
        throw new Error(errMsg);
      }

      const responseText = await response.text();
      /* console.log('Product creation response:', responseText); */

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(`API returned non-JSON response: ${responseText.substring(0, 200)}`);
      }

      return data;
    } catch (error) {
      /* console.error('Error creating product:', error); */
      if (!navigator.onLine) {
        // Fallback catch if network drops during request
        const { addToSyncQueue } = require('./db');
        await addToSyncQueue({
          action: 'CREATE_PRODUCT',
          data: productData
        });
        return { success: true, message: 'Product saved offline. Will sync when internet is restored.' };
      }
      throw error;
    }
  }

  // Get product edit data from API — GET /connector/api/product/{id} or /products/{id}/edit
  async getEditProductData(productId) {
    try {
      const token = await this.getAuthToken();
      // Try core connector product show route first GET /connector/api/product/{id}
      let response = await fetch(`${this.baseURL}/connector/api/product/${productId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      if (!response.ok) {
        // Fallback to /products/{id}/edit or /product/{id}/edit
        response = await fetch(`${this.baseURL}/connector/api/products/${productId}/edit`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
      }
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/connector/api/product/${productId}/edit`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });
      }
      if (!response.ok) throw new Error(`Failed to fetch product edit data: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.warn('Error fetching product edit data:', error); */
      return null;
    }
  }

  // Update an existing product
  async updateProduct(productId, productData) {
    try {
      const token = await this.getAuthToken();
      let url = `${this.baseURL}/connector/api/products/${productId}`;
      /* console.log('Updating product with data:', productData); */

      const formData = new FormData();
      formData.append('_method', 'PUT');

      Object.keys(productData).forEach(key => {
        if (productData[key] !== null && productData[key] !== undefined) {
          if (Array.isArray(productData[key])) {
            productData[key].forEach((val, idx) => {
              formData.append(`${key}[${idx}]`, val);
            });
          } else if (typeof productData[key] === 'object' && !(productData[key] instanceof File)) {
            Object.keys(productData[key]).forEach(subKey => {
              formData.append(`${key}[${subKey}]`, productData[key][subKey]);
            });
          } else {
            formData.append(key, productData[key]);
          }
        }
      });

      let response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok && response.status === 404) {
        // Fallback to singular connector/api/product/{id}
        url = `${this.baseURL}/connector/api/product/${productId}`;
        response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
      }

      if (!response.ok) {
        const errorText = await response.text();
        /* console.error('Product update error:', errorText); */
        let errMsg = `Failed to update product: ${response.status}`;
        try {
          const errJson = JSON.parse(errorText);
          errMsg = errJson.message || errMsg;
        } catch (_) { }
        throw new Error(errMsg);
      }

      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        /* console.error('Failed to parse product update response:', parseError); */
        throw new Error(`API returned non-JSON response: ${responseText.substring(0, 200)}...`);
      }

      return data;
    } catch (error) {
      /* console.error('Error updating product:', error); */
      throw error;
    }
  }



  // Get payment accounts from API
  async getPaymentAccounts(params = {}) {
    try {
      const token = await this.getAuthToken();

      const query = new URLSearchParams();
      if (params.page) query.append('page', params.page);
      if (params.per_page) query.append('per_page', params.per_page);
      if (params.account_status) query.append('account_status', params.account_status);
      if (params.location_id) query.append('location_id', params.location_id);

      const url = `${this.baseURL}/connector/api/payment-accounts?${query.toString()}`;
      /* console.log('[DEBUG] getPaymentAccounts URL:', url); */

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch payment accounts: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching payment accounts:', error); */
      throw error;
    }
  }

  async createPaymentAccount(accountData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/payment-accounts`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(accountData)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create payment account: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error creating payment account:', error); */
      throw error;
    }
  }

  async updatePaymentAccount(accountId, accountData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/payment-accounts/${accountId}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(accountData)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update payment account: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error updating payment account:', error); */
      throw error;
    }
  }

  async deletePaymentAccount(accountId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/payment-accounts/${accountId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete payment account: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting payment account:', error); */
      throw error;
    }
  }

  async getAccountTypes() {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/account-types`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch account types: ${response.status} - ${errorText}`);
      }
      const data = await response.json();
      return data?.data || data || [];
    } catch (error) {
      /* console.error('Error fetching account types:', error); */
      throw error;
    }
  }

  async getNotifications() {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/notifications`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch notifications: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching notifications:', error); */
      throw error;
    }
  }

  async getAccountBook(accountId, params = {}) {
    try {
      const token = await this.getAuthToken();
      const query = new URLSearchParams();
      if (params.start_date) query.append('start_date', params.start_date);
      if (params.end_date) query.append('end_date', params.end_date);
      if (params.type) query.append('type', params.type);
      if (params.page) query.append('page', params.page);
      if (params.per_page) query.append('per_page', params.per_page);
      const url = `${this.baseURL}/connector/api/account-book/${accountId}?${query.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch account book: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching account book:', error); */
      throw error;
    }
  }

  async deleteAccountTransaction(id) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/account-transactions/${id}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete transaction: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting account transaction:', error); */
      throw error;
    }
  }

  async getAccountTransactionForEdit(id) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/account-transactions/${id}/edit`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch transaction for edit: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching transaction for edit:', error); */
      throw error;
    }
  }

  async updateAccountTransaction(id, data) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/account-transactions/${id}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update transaction: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error updating account transaction:', error); */
      throw error;
    }
  }

  async postFundTransfer(data) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/fund-transfer`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to transfer funds: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error transferring funds:', error); */
      throw error;
    }
  }

  async postDeposit(data) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/deposit`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to deposit: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error depositing:', error); */
      throw error;
    }
  }

  async closeAccount(accountId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/close-account/${accountId}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to close account: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error closing account:', error); */
      throw error;
    }
  }

  async createAccountType(data) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/account-types`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create account type: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error creating account type:', error); */
      throw error;
    }
  }

  async updateAccountType(id, data) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/account-types/${id}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update account type: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error updating account type:', error); */
      throw error;
    }
  }

  async deleteAccountType(id) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/account-types/${id}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete account type: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting account type:', error); */
      throw error;
    }
  }

  async getBalanceSheet(params = {}) {
    try {
      const token = await this.getAuthToken();
      const query = new URLSearchParams();
      if (params.end_date) query.append('end_date', params.end_date);
      if (params.location_id) query.append('location_id', params.location_id);
      const url = `${this.baseURL}/connector/api/balance-sheet?${query.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch balance sheet: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching balance sheet:', error); */
      throw error;
    }
  }

  async getTrialBalance(params = {}) {
    try {
      const token = await this.getAuthToken();
      const query = new URLSearchParams();
      if (params.end_date) query.append('end_date', params.end_date);
      if (params.location_id) query.append('location_id', params.location_id);
      const url = `${this.baseURL}/connector/api/trial-balance?${query.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch trial balance: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching trial balance:', error); */
      throw error;
    }
  }

  async getCashFlow(params = {}) {
    try {
      const token = await this.getAuthToken();
      const query = new URLSearchParams();
      if (params.account_id) query.append('account_id', params.account_id);
      if (params.type) query.append('type', params.type);
      if (params.start_date) query.append('start_date', params.start_date);
      if (params.end_date) query.append('end_date', params.end_date);
      if (params.location_id) query.append('location_id', params.location_id);
      const url = `${this.baseURL}/connector/api/cash-flow?${query.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch cash flow: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching cash flow:', error); */
      throw error;
    }
  }

  async getPaymentAccountReport(params = {}) {
    try {
      const token = await this.getAuthToken();
      const query = new URLSearchParams();
      if (params.start_date) query.append('start_date', params.start_date);
      if (params.end_date) query.append('end_date', params.end_date);
      if (params.account_id) query.append('account_id', params.account_id);
      if (params.type) query.append('type', params.type);
      if (params.location_id) query.append('location_id', params.location_id);
      if (params.page) query.append('page', params.page);
      if (params.per_page) query.append('per_page', params.per_page);
      if (params.sort_column) query.append('sort_column', params.sort_column);
      if (params.sort_direction) query.append('sort_direction', params.sort_direction);
      const url = `${this.baseURL}/connector/api/payment-account-report?${query.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch payment account report: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching payment account report:', error); */
      throw error;
    }
  }

  async linkPaymentAccount(payload) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/link-payment-account`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to link account: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error linking account:', error); */
      throw error;
    }
  }

  // Get contacts from API with optional search
  async getContacts(searchTerm = {}) {
    let paramsInit = {};
    try {
      if (!navigator.onLine) {
        const { getContactsFromOfflineDB } = require('./db');
        const offlineContacts = await getContactsFromOfflineDB();
        return { data: offlineContacts };
      }

      const token = await this.getAuthToken();

      // Handle legacy string argument
      if (typeof searchTerm === 'string') {
        paramsInit = { name: searchTerm, per_page: searchTerm.trim() ? '50' : '100' };
      } else {
        paramsInit = { ...searchTerm }; // It's an object
      }

      const params = new URLSearchParams();

      if (paramsInit.name) params.append('name', paramsInit.name);
      if (paramsInit.type) params.append('type', paramsInit.type);
      if (paramsInit.per_page) params.append('per_page', paramsInit.per_page);
      if (paramsInit.page) params.append('page', paramsInit.page);

      // Default per_page if not set
      if (!params.has('per_page')) params.append('per_page', '100');

      const url = `${this.baseURL}/connector/api/contactapi?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch contacts: ${response.status}`);
      }

      const data = await response.json();

      // Save to offline DB
      try {
        const { saveContactsToOfflineDB } = require('./db');
        const contactsToSave = data.data || data || [];
        await saveContactsToOfflineDB(contactsToSave);
      } catch (dbErr) {
        /* console.error('Failed to cache contacts', dbErr); */
      }

      return data;
    } catch (error) {
      /* console.error('Error fetching contacts:', error); */
      // Fallback to offline DB if network fails despite navigator.onLine being true
      try {
        const { getContactsFromOfflineDB } = require('./db');
        const offlineContacts = await getContactsFromOfflineDB();
        if (offlineContacts && offlineContacts.length > 0) {
          return { data: offlineContacts };
        }
      } catch (dbErr) { }
      throw error;
    }
  }

  // Create a new contact (Supplier or Customer)
  async createContact(contactData) {
    try {
      if (!navigator.onLine) {
        const { addToSyncQueue } = require('./db');
        await addToSyncQueue({
          action: 'CREATE_CONTACT',
          data: contactData
        });
        /* console.log('Offline: Contact queued for sync'); */
        return { success: true, message: 'Contact saved offline. Will sync when internet is restored.' };
      }

      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/contactapi`;
      /* console.log('Creating contact with data:', contactData); */

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(contactData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        /* console.error('Contact creation error:', errorText); */
        let errMsg = `Failed to create contact: ${response.status}`;
        try {
          const errJson = JSON.parse(errorText);
          if (errJson.errors) {
            const errorDetails = Object.entries(errJson.errors)
              .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
              .join('; ');
            errMsg = `Validation failed: ${errorDetails}`;
          } else if (errJson.error && errJson.error.message) {
            errMsg = errJson.error.message;
          } else {
            errMsg = errJson.message || errMsg;
          }
        } catch (_) {
          errMsg = `Server Error: ${errorText.substring(0, 150)}...`;
        }
        throw new Error(errMsg);
      }

      return await response.json();
    } catch (error) {
      /* console.error('Error creating contact:', error); */
      if (!navigator.onLine) {
        // Fallback catch if network drops during request
        const { addToSyncQueue } = require('./db');
        await addToSyncQueue({
          action: 'CREATE_CONTACT',
          data: contactData
        });
        return { success: true, message: 'Contact saved offline. Will sync when internet is restored.' };
      }
      throw error;
    }
  }

  // Update an existing contact
  async updateContact(contactId, contactData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/contactapi/${contactId}`;
      /* console.log(`Updating contact ${contactId} with data:`, contactData); */

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(contactData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        /* console.error('Contact update error:', errorText); */
        let errMsg = `Failed to update contact: ${response.status}`;
        try {
          const errJson = JSON.parse(errorText);
          if (errJson.errors) {
            const errorDetails = Object.entries(errJson.errors)
              .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
              .join('; ');
            errMsg = `Validation failed: ${errorDetails}`;
          } else {
            errMsg = errJson.message || errMsg;
          }
        } catch (_) { /* not JSON */ }
        throw new Error(errMsg);
      }

      return await response.json();
    } catch (error) {
      /* console.error('Error updating contact:', error); */
      throw error;
    }
  }

  // Delete a contact
  async deleteContact(contactId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/contactapi/${contactId}`;
      /* console.log(`Deleting contact ${contactId}`); */

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        /* console.error('Contact deletion error:', errorText); */
        let errMsg = `Failed to delete contact: ${response.status}`;
        try {
          const errJson = JSON.parse(errorText);
          errMsg = errJson.message || errMsg;
        } catch (_) { /* not JSON */ }
        throw new Error(errMsg);
      }

      return await response.json();
    } catch (error) {
      /* console.error('Error deleting contact:', error); */
      throw error;
    }
  }

  // Get customer by ID to get full details including balance
  async getCustomerById(customerId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/contactapi/${customerId}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch customer details: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching customer details:', error); */
      throw error;
    }
  }

  // Get contact ledger
  async getContactLedger(contactId, startDate, endDate, format = 'format_1', locationId = null) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      queryParams.append('contact_id', contactId);
      if (startDate) queryParams.append('start_date', startDate);
      if (endDate) queryParams.append('end_date', endDate);
      if (locationId && locationId !== 'all') queryParams.append('location_id', locationId);
      queryParams.append('ledger_format', format);
      queryParams.append('_t', Date.now()); // cache buster

      const url = `${this.baseURL}/connector/api/contactapi-ledger?${queryParams.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch contact ledger: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching contact ledger:', error); */
      throw error;
    }
  }

  // Get supplier stock report
  async getSupplierStockReport(supplierId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/supplier-stock-report/${supplierId}?_t=${Date.now()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        },
        cache: 'no-store'
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch supplier stock report: ${response.status}`);
      }
      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching supplier stock report:', error); */
      throw error;
    }
  }

  // Get tax rates from API
  async getTaxRates() {
    try {
      const token = await this.getAuthToken();

      const url = `${this.baseURL}/connector/api/tax`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tax rates: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching tax rates:', error); */
      throw error;
    }
  }

  // Get invoice schemes
  async getInvoiceSchemes() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/invoice-schema`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch invoice schemes: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching invoice schemes:', error); */
      throw error;
    }
  }

  // ===== Connector Invoice Schemes =====
  async getInvoiceSchemesConnector() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/invoice-schemes`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch invoice schemes: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching invoice schemes:', error); */
      throw error;
    }
  }

  async createInvoiceScheme(schemeData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/invoice-schemes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(schemeData)
      });
      if (!response.ok) throw new Error(`Failed to create invoice scheme: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error creating invoice scheme:', error); */
      throw error;
    }
  }

  async updateInvoiceScheme(id, schemeData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/invoice-schemes/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(schemeData)
      });
      if (!response.ok) throw new Error(`Failed to update invoice scheme: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error updating invoice scheme:', error); */
      throw error;
    }
  }

  async deleteInvoiceScheme(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/invoice-schemes/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to delete invoice scheme: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting invoice scheme:', error); */
      throw error;
    }
  }

  async setDefaultInvoiceScheme(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/invoice-schemes/${id}/set-default`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to set default invoice scheme: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error setting default invoice scheme:', error); */
      throw error;
    }
  }

  // ===== Connector Invoice Layouts =====
  async getInvoiceLayouts() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/invoice-layouts`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch invoice layouts: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching invoice layouts:', error); */
      throw error;
    }
  }

  async getInvoiceLayout(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/invoice-layouts/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch invoice layout: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching invoice layout:', error); */
      throw error;
    }
  }

  async createInvoiceLayout(layoutData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/invoice-layouts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: layoutData // FormData for file uploads
      });
      if (!response.ok) throw new Error(`Failed to create invoice layout: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error creating invoice layout:', error); */
      throw error;
    }
  }

  async updateInvoiceLayout(id, layoutData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/invoice-layouts/${id}`, {
        method: 'POST', // Laravel resource route PUT via POST with _method
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: layoutData // FormData for file uploads
      });
      if (!response.ok) throw new Error(`Failed to update invoice layout: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error updating invoice layout:', error); */
      throw error;
    }
  }

  async deleteInvoiceLayout(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/invoice-layouts/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to delete invoice layout: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting invoice layout:', error); */
      throw error;
    }
  }

  async bulkDeleteInvoiceLayouts(layoutIds) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/invoice-layouts/bulk-delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ layout_ids: layoutIds })
      });
      if (!response.ok) throw new Error(`Failed to bulk delete layouts: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error bulk deleting invoice layouts:', error); */
      throw error;
    }
  }

  async setDefaultInvoiceLayout(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/invoice-layouts/${id}/set-default`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to set default layout: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error setting default invoice layout:', error); */
      throw error;
    }
  }

  async getInvoiceLayoutDesigns() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/invoice-layouts/designs`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch layout designs: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching layout designs:', error); */
      throw error;
    }
  }

  // ===== Connector Barcode Settings =====
  async getBarcodeSettings() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/barcodes`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch barcode settings: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching barcode settings:', error); */
      throw error;
    }
  }

  async getBarcodeSetting(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/barcodes/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch barcode setting: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching barcode setting:', error); */
      throw error;
    }
  }

  async createBarcodeSetting(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/barcodes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to create barcode setting: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error creating barcode setting:', error); */
      throw error;
    }
  }

  async updateBarcodeSetting(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/barcodes/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to update barcode setting: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error updating barcode setting:', error); */
      throw error;
    }
  }

  async deleteBarcodeSetting(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/barcodes/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to delete barcode setting: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting barcode setting:', error); */
      throw error;
    }
  }

  async setDefaultBarcodeSetting(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/barcodes/${id}/set-default`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to set default barcode setting: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error setting default barcode setting:', error); */
      throw error;
    }
  }

  // ===== Connector Receipt Printers =====
  async getPrinters() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/printers`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch printers: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching printers:', error); */
      throw error;
    }
  }

  async getPrinter(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/printers/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch printer: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching printer:', error); */
      throw error;
    }
  }

  async createPrinter(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/printers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to create printer: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error creating printer:', error); */
      throw error;
    }
  }

  async updatePrinter(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/printers/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to update printer: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error updating printer:', error); */
      throw error;
    }
  }

  async deletePrinter(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/printers/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to delete printer: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting printer:', error); */
      throw error;
    }
  }

  // ===== Connector Tax Rates =====
  async getTaxRates() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/tax-rates`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch tax rates: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching tax rates:', error); */
      throw error;
    }
  }

  async getTaxRate(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/tax-rates/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch tax rate: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching tax rate:', error); */
      throw error;
    }
  }

  async createTaxRate(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/tax-rates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to create tax rate: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error creating tax rate:', error); */
      throw error;
    }
  }

  async updateTaxRate(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/tax-rates/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to update tax rate: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error updating tax rate:', error); */
      throw error;
    }
  }

  async deleteTaxRate(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/tax-rates/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to delete tax rate: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting tax rate:', error); */
      throw error;
    }
  }

  // ===== Connector Tax Groups =====
  async getTaxGroups() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/tax-groups`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch tax groups: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching tax groups:', error); */
      throw error;
    }
  }

  async getTaxGroup(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/tax-groups/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch tax group: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching tax group:', error); */
      throw error;
    }
  }

  async createTaxGroup(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/tax-groups`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to create tax group: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error creating tax group:', error); */
      throw error;
    }
  }

  async updateTaxGroup(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/tax-groups/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to update tax group: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error updating tax group:', error); */
      throw error;
    }
  }

  async deleteTaxGroup(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/tax-groups/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to delete tax group: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting tax group:', error); */
      throw error;
    }
  }



  // Convert draft/quotation to proforma
  async convertToProforma(saleId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/sells/${saleId}/convert-to-proforma`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success === 0) {
        throw new Error(data.msg || 'Failed to convert to proforma');
      }

      return data;
    } catch (error) {
      /* console.error('Error converting to proforma:', error); */
      throw error;
    }
  }

  // Update existing sell data via API
  async updateSell(saleId, sellData) {
    try {
      const token = await this.getAuthToken();
      let url = `${this.baseURL}/connector/api/sells/${saleId}`;
      /* console.log('Updating sale at:', url); */
      /* console.log('Update data:', sellData); */

      let response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(sellData)
      });

      if (!response.ok && response.status === 404) {
        /* console.warn(`Update via /connector/api/sells/${saleId} returned 404. Trying fallback endpoint /connector/api/sell/${saleId}`); */
        url = `${this.baseURL}/connector/api/sell/${saleId}`;
        response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(sellData)
        });
      }

      if (!response.ok) {
        const errorText = await response.text();
        /* console.error('Error updating sale:', errorText); */

        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) { }

        let rawMsg = errorText;
        if (errorData) {
          if (typeof errorData.message === 'string') {
            rawMsg = errorData.message;
          } else if (typeof errorData.error === 'string') {
            rawMsg = errorData.error;
          } else if (errorData.messages) {
            rawMsg = Object.values(errorData.messages).flat().join('; ');
          }
        }

        const cleanMsg = typeof rawMsg === 'string'
          ? rawMsg.replace(/<br\s*\/?>/gi, ' ').replace(/<\/?[^>]+(>|$)/g, '').replace(/^{.*}$/g, '')
          : rawMsg;

        throw new Error(cleanMsg || 'Failed to update sale');
      }

      const data = await response.json();
      /* console.log('Update response:', data); */
      return data;
    } catch (error) {
      /* console.error('Error updating sale:', error); */
      throw error;
    }
  }

  // Update shipping status API
  async updateShippingStatus(transactionId, shippingStatus) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/update-shipping-status`;
      /* console.log('Updating shipping status for transaction:', transactionId, 'to:', shippingStatus); */

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          transaction_id: parseInt(transactionId),
          shipping_status: shippingStatus
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        /* console.error('Error updating shipping status:', errorText); */
        throw new Error(`Failed to update shipping status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      /* console.log('Shipping status update response:', data); */
      return data;
    } catch (error) {
      /* console.error('Error updating shipping status:', error); */
      throw error;
    }
  }

  // Save shipping details separately
  async saveShippingDetails(saleId, shippingData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/api/shipping-details`;
      /* console.log('Saving shipping details for sale:', saleId); */
      /* console.log('Shipping data:', shippingData); */

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sale_id: saleId,
          ...shippingData
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        /* console.error('Error saving shipping details:', errorText); */
        throw new Error(`Failed to save shipping details: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      /* console.log('Shipping details saved:', data); */
      return data;
    } catch (error) {
      /* console.error('Error saving shipping details:', error); */
      throw error;
    }
  }

  // Get products from API
  async getProducts(params = {}) {
    try {
      if (!navigator.onLine) {
        /* console.log('Offline: Serving products from IndexedDB cache'); */
        const { getProductsFromOfflineDB } = require('./db');
        const offlineProducts = await getProductsFromOfflineDB();
        return { data: offlineProducts };
      }

      const token = await this.getAuthToken();

      // Handle legacy string argument
      if (typeof params === 'string') {
        params = { search: params };
      }

      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);

      // Default to 25 if not specific (or use what is passed, -1 for all)
      if (params.per_page) {
        queryParams.append('per_page', params.per_page);
      } else {
        queryParams.append('per_page', '25');
      }

      if (params.search) {
        // Support multiple common search params to ensure SKU is caught by backend
        queryParams.append('name', params.search);
        queryParams.append('search', params.search);
        queryParams.append('sku', params.search);
        queryParams.append('term', params.search);
      }

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const url = `${this.baseURL}/connector/api/product${queryString}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.status}`);
      }

      const data = await response.json();
      /* console.log('Products fetched:', Array.isArray(data) ? data.length : (data.data ? data.data.length : 0)); */

      // Save to offline DB
      try {
        const { saveProductsToOfflineDB } = require('./db');
        const productsToSave = data.data || data || [];
        await saveProductsToOfflineDB(productsToSave);
      } catch (dbErr) {
        /* console.error('Failed to cache products', dbErr); */
      }

      return data;
    } catch (error) {
      /* console.error('Error fetching products:', error); */
      // Fallback to offline DB if network fails despite navigator.onLine being true
      try {
        const { getProductsFromOfflineDB } = require('./db');
        const offlineProducts = await getProductsFromOfflineDB();
        if (offlineProducts && offlineProducts.length > 0) {
          return { data: offlineProducts };
        }
      } catch (dbErr) { }
      throw error;
    }
  }

  // Get single product details
  async getProduct(productId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/product/${productId}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching product:', error); */
      throw error;
    }
  }


  // Get payment methods from API
  async getPaymentMethods(location_id = null) {
    try {
      const token = await this.getAuthToken();

      const queryParams = new URLSearchParams();
      if (location_id) queryParams.append('location_id', location_id);
      const url = `${this.baseURL}/connector/api/payment-methods${queryParams.toString() ? '?' + queryParams.toString() : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch payment methods: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching payment methods:', error); */
      throw error;
    }
  }

  // Get sales from API
  // Get sales from API with filters
  async getSales(params = {}) {
    try {
      const token = await this.getAuthToken();

      const queryParams = new URLSearchParams();
      // Default to all records unless specified
      queryParams.append('per_page', params.per_page || '-1');

      // Include product and payment details in the response
      queryParams.append('with[]', 'contact');
      queryParams.append('with[]', 'location');
      queryParams.append('with[]', 'product');
      queryParams.append('with[]', 'sell_lines.product');
      queryParams.append('with[]', 'sell_lines.variations');
      queryParams.append('with[]', 'sell_lines.variations.product');
      queryParams.append('with[]', 'payment_lines');

      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.location_id && params.location_id !== 'all') queryParams.append('location_id', params.location_id);
      if (params.contact_id && params.contact_id !== 'all') queryParams.append('contact_id', params.contact_id);
      if (params.status && params.status !== 'all') queryParams.append('status', params.status);
      if (params.payment_status && params.payment_status !== 'all') queryParams.append('payment_status', params.payment_status);
      if (params.user_id && params.user_id !== 'all') queryParams.append('user_id', params.user_id);
      if (params.shipping_status && params.shipping_status !== 'all') queryParams.append('shipping_status', params.shipping_status);
      if (params.source && params.source !== 'all') queryParams.append('source', params.source);
      if (params.is_direct_sale !== undefined && params.is_direct_sale !== null) queryParams.append('is_direct_sale', params.is_direct_sale);
      if (params.sub_type) queryParams.append('sub_type', params.sub_type);
      if (params.only_shipments !== undefined && params.only_shipments !== null) queryParams.append('only_shipments', params.only_shipments);
      if (params.only_pending_shipments !== undefined && params.only_pending_shipments !== null) queryParams.append('only_pending_shipments', params.only_pending_shipments);
      if (params.only_sales_order !== undefined && params.only_sales_order !== null) queryParams.append('only_sales_order', params.only_sales_order);
      if (params.per_page !== undefined && params.per_page !== null) queryParams.append('per_page', params.per_page);
      if (params.search && params.search.trim()) queryParams.append('search', params.search.trim());

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const url = `${this.baseURL}/connector/api/sell${queryString}`;
      /* console.log('Fetching sales with params:', url); */

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch sales: ${response.status}`);
      }

      const data = await response.json();
      /* console.log('Sales fetched:', Array.isArray(data) ? data.length : (data.data ? data.data.length : 0)); */
      return data;
    } catch (error) {
      /* console.error('Error fetching sales:', error); */
      throw error;
    }
  }

  // Save / Create a sale (POS / Add Sell)
  async saveSell(sellData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/sell`;

      // Wrap in sells array if not already wrapped
      const payload = sellData && sellData.sells ? sellData : { sells: [sellData] };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Failed to save sale: ${response.status} - ${errText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error saving sell:', error); */
      throw error;
    }
  }

  async createSell(sellData) {
    return this.saveSell(sellData);
  }

  async createSale(sellData) {
    return this.saveSell(sellData);
  }

  async savePOSSale(sellData) {
    return this.saveSell(sellData);
  }

  // Update an existing sale
  async updateSell(saleId, sellData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/sell/${saleId}`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(sellData)
      });

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`Failed to update sale: ${response.status} - ${errText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error updating sell:', error); */
      throw error;
    }
  }

  async updateSale(saleId, sellData) {
    return this.updateSell(saleId, sellData);
  }

  // Delete a sale
  async deleteSale(saleId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/sell/${saleId}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        let errorText = await response.text();
        try {
          const errorJson = JSON.parse(errorText);
          errorText = errorJson.message || JSON.stringify(errorJson) || errorText;
        } catch (e) { }
        throw new Error(`${response.status} - ${errorText}`);
      }

      try {
        window.dispatchEvent(new Event('reload-subscription'));
      } catch (e) {
        /* console.error('Error dispatching reload-subscription on sale delete:', e); */
      }

      return await response.json();
    } catch (error) {
      /* console.error('Error deleting sale:', error); */
      throw error;
    }
  }

  // Get suspended sales (Hold Sales)
  async getSuspendedSales(params = {}) {
    try {
      const token = await this.getAuthToken();
      let url = `${this.baseURL}/connector/api/sell/suspended`;
      const queryParams = new URLSearchParams();
      if (params.location_id && params.location_id !== 'all') {
        queryParams.append('location_id', params.location_id);
      }
      if (queryParams.toString()) {
        url += `?${queryParams.toString()}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch suspended sales: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching suspended sales:', error); */
      throw error;
    }
  }

  // Delete a product
  async deleteProduct(productId) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/product/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Fallback 1: POST to /connector/api/product/{id}/delete if DELETE verb is blocked by web server
      if (response.status === 405 || response.status === 404) {
        response = await fetch(`${this.baseURL}/connector/api/product/${productId}/delete`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }

      // Fallback 2: POST to /connector/api/product/{id} with _method DELETE
      if (response.status === 405) {
        response = await fetch(`${this.baseURL}/connector/api/product/${productId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ _method: 'DELETE' })
        });
      }

      if (!response.ok) {
        let errorText = await response.text();
        try {
          const errorJson = JSON.parse(errorText);
          errorText = errorJson.message || JSON.stringify(errorJson) || errorText;
        } catch (e) { }
        throw new Error(errorText || `Failed to delete product (${response.status})`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting product:', error); */
      throw error;
    }
  }

  // Create Sell Return
  async createSellReturn(returnData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/sell-return`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(returnData)
      });

      if (!response.ok) {
        let errorText = await response.text();
        try {
          const errorJson = JSON.parse(errorText);
          errorText = errorJson.message || JSON.stringify(errorJson) || errorText;
        } catch (e) { }
        throw new Error(`${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      /* console.error('Error creating sell return:', error); */
      throw error;
    }
  }

  // Get sell returns from API with filters
  async getSellReturns(params = {}) {
    try {
      const token = await this.getAuthToken();

      const queryParams = new URLSearchParams();
      // Default to all records unless specified
      queryParams.append('per_page', params.per_page || '-1');

      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.location_id && params.location_id !== 'all') queryParams.append('location_id', params.location_id);
      if (params.contact_id && params.contact_id !== 'all') queryParams.append('contact_id', params.contact_id);
      if (params.payment_status && params.payment_status !== 'all') queryParams.append('payment_status', params.payment_status);
      if (params.user_id && params.user_id !== 'all') queryParams.append('user_id', params.user_id);
      if (params.shipping_status && params.shipping_status !== 'all') queryParams.append('shipping_status', params.shipping_status);
      if (params.source && params.source !== 'all') queryParams.append('source', params.source);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const url = `${this.baseURL}/connector/api/list-sell-return${queryString}`;
      /* console.log('Fetching sell returns with params:', url); */

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch sell returns: ${response.status}`);
      }

      const data = await response.json();
      /* console.log('Sell returns fetched:', Array.isArray(data) ? data.length : (data.data ? data.data.length : 0)); */
      return data;
    } catch (error) {
      /* console.error('Error fetching sell returns:', error); */
      throw error;
    }
  }

  // Clear stored token (for logout)
  clearToken() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    localStorage.removeItem(BUSINESS_TIMEZONE_KEY);
    localStorage.removeItem('etpos_date_format');
    localStorage.removeItem('etpos_time_format');
    localStorage.removeItem('etpos_currency_precision');
    localStorage.removeItem('etpos_quantity_precision');
    localStorage.removeItem('etpos_currency_placement');
    localStorage.removeItem('etpos_currency_symbol');
    localStorage.removeItem('etpos_decimal_separator');
    localStorage.removeItem('etpos_thousand_separator');
  }

  // Check if token is valid
  isTokenValid() {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return false;
    const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!tokenExpiry) {
      const expiryTime = new Date();
      expiryTime.setHours(expiryTime.getHours() + 24);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toISOString());
      return true;
    }
    return new Date(tokenExpiry) > new Date();
  }

  // Get users from API with pagination and filters
  async getUsers(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();

      // Add pagination and filter parameters
      if (params.page) queryParams.append('page', params.page);
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.search) queryParams.append('search', params.search);
      if (params.role_id) queryParams.append('role_id', params.role_id);
      if (params.business_id) queryParams.append('business_id', params.business_id);

      const url = `${this.baseURL}/connector/api/users?${queryParams.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Failed to fetch users: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching users:', error); */
      throw error;
    }
  }

  // Create new user
  async createUser(userData) {
    /* console.log('CREATE USER API - Input userData:', userData); */
    try {
      const token = await this.getAuthToken();
      /* console.log('CREATE USER API - Token obtained:', token ? 'success' : 'failed'); */

      const requestBody = JSON.stringify(userData);
      const response = await fetch(`${this.baseURL}/connector/api/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: requestBody
      });

      const apiResponseData = await response.json();
      if (!response.ok) {
        let detailedError = apiResponseData.message || `Failed to create user: ${response.status}`;
        if (apiResponseData.errors) {
          const errorDetails = Object.entries(apiResponseData.errors)
            .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
            .join('; ');
          detailedError = `Validation failed: ${errorDetails}`;
        }
        throw new Error(detailedError);
      }

      return apiResponseData;
    } catch (error) {
      /* console.error('CREATE USER API - Error:', error); */
      throw error;
    }
  }

  // Get user for editing
  async getUserForEdit(userId) {
    /* console.log('GET USER FOR EDIT API - Input userId:', userId); */
    try {
      const token = await this.getAuthToken();
      const userDetails = await this.getUserDetails();
      const businessId = userDetails?.data?.business_id || userDetails?.business_id || 40;

      // Add business_id parameter and a 3 second timeout so the UI never hangs
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      const response = await fetch(`${this.baseURL}/connector/api/users/${userId}/edit?business_id=${businessId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Failed to fetch user: ${response.status}`);
      }

      const apiResponseData = await response.json();
      /* console.log('GET USER FOR EDIT API - Response data:', apiResponseData); */
      return apiResponseData;
    } catch (error) {
      /* console.error('GET USER FOR EDIT API - Error:', error); */
      throw error;
    }
  }

  // Update user
  async updateUser(userId, userData) {
    /* console.log('UPDATE USER API - Input userId:', userId); */
    /* console.log('UPDATE USER API - Input userData:', userData); */
    try {
      const token = await this.getAuthToken();
      /* console.log('UPDATE USER API - Token obtained:', token ? 'success' : 'failed'); */

      const requestBody = JSON.stringify(userData);
      const response = await fetch(`${this.baseURL}/connector/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: requestBody
      });

      const apiResponseData = await response.json();
      if (!response.ok) {
        let detailedError = apiResponseData.message || `Failed to update user: ${response.status}`;
        if (apiResponseData.errors) {
          const errorDetails = Object.entries(apiResponseData.errors)
            .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
            .join('; ');
          detailedError = `Validation failed: ${errorDetails}`;
        }
        throw new Error(detailedError);
      }

      return apiResponseData;
    } catch (error) {
      /* console.error('UPDATE USER API - Error:', error); */
      throw error;
    }
  }

  // Delete user
  async deleteUser(userId) {
    /* console.log('DELETE USER API - Input userId:', userId); */
    try {
      const token = await this.getAuthToken();
      const userDetails = await this.getUserDetails();
      const businessId = userDetails?.data?.business_id || userDetails?.business_id || 40;
      /* console.log('DELETE USER API - Token obtained:', token ? 'success' : 'failed'); */
      /* console.log('DELETE USER API - Business ID:', businessId); */

      // Try DELETE method with business_id parameter
      let response = await fetch(`${this.baseURL}/connector/api/users/${userId}?business_id=${businessId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      /* console.log('DELETE USER API - DELETE Response status:', response.status); */
      /* console.log('DELETE USER API - DELETE Response ok:', response.ok); */

      // If DELETE fails with 405, try POST with _method=DELETE and business_id
      if (!response.ok && response.status === 405) {
        /* console.log('DELETE USER API - DELETE not allowed, trying POST with _method=DELETE'); */

        response = await fetch(`${this.baseURL}/connector/api/users/${userId}?business_id=${businessId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: new URLSearchParams({
            '_method': 'DELETE',
            'business_id': businessId.toString()
          })
        });
        /* console.log('DELETE USER API - POST Response status:', response.status); */
        /* console.log('DELETE USER API - POST Response ok:', response.ok); */
      }
      /* console.log('DELETE USER API - Final Response status:', response.status); */
      /* console.log('DELETE USER API - Final Response ok:', response.ok); */

      if (!response.ok) {
        // Handle both JSON and HTML responses
        const contentType = response.headers.get('content-type');
        let errorData;

        if (contentType && contentType.includes('application/json')) {
          errorData = await response.json();
        } else {
          // Handle HTML response (error page)
          const htmlText = await response.text();
          /* console.log('DELETE USER API - HTML response:', htmlText.substring(0, 200)); */
          errorData = { message: `Delete not supported by backend (${response.status})` };
        }

        throw new Error(errorData.message || `Failed to delete user: ${response.status}`);
      }

      const apiResponseData = await response.json();
      /* console.log('DELETE USER API - Response data:', apiResponseData); */

      return apiResponseData;
    } catch (error) {
      /* console.error('DELETE USER API - Error:', error); */
      throw error;
    }
  }

  // Get user roles and permissions
  async getUserRoles(businessId = null) {
    /* console.log('🔐 RBAC DEBUG - Fetching user roles...'); */
    try {
      const token = await this.getAuthToken();
      /* console.log('🔐 RBAC DEBUG - Token obtained for user roles:', token ? 'success' : 'failed'); */

      if (!token) {
        /* console.error('🔐 RBAC DEBUG - No token available for user roles'); */
        return { data: [] };
      }

      const userDetails = await this.getUserDetails();
      const userId = userDetails.data?.id || userDetails.id;
      const userBusinessId = businessId || userDetails.data?.business_id || userDetails.business_id;
      /* console.log('🔐 RBAC DEBUG - User ID:', userId); */
      /* console.log('🔐 RBAC DEBUG - Business ID:', userBusinessId); */

      if (!userId) {
        /* console.log('🔐 RBAC DEBUG - No user ID found, cannot fetch roles'); */
        return { data: [] };
      }

      // Try to get user-specific roles
      const url = `${this.roleBaseURL}/connector/api/users/${userId}/roles?business_id=${userBusinessId}`;
      /* console.log('🔐 RBAC DEBUG - User roles URL:', url); */

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      /* console.log('🔐 RBAC DEBUG - User roles response status:', response.status); */
      /* console.log('🔐 RBAC DEBUG - User roles response ok:', response.ok); */

      if (!response.ok) {
        /* console.log('🔐 RBAC DEBUG - User roles API failed, falling back to general roles'); */
        // Fallback to general roles dropdown
        return await this.getRolesDropdown(userBusinessId);
      }

      const data = await response.json();
      /* console.log('🔐 RBAC DEBUG - User roles response data:', data); */

      if (data.data) {
        data.data.forEach((role, index) => {
          /* console.log(`🔐 RBAC DEBUG - User Role ${index + 1}:`, role); */
          if (role.permissions) {
            /* console.log(`🔐 RBAC DEBUG - Role ${index + 1} permissions:`, role.permissions); */
          }
        });
      }
      /* console.log('🔐 RBAC DEBUG - getUserRoles completed successfully'); */
      return data;
    } catch (error) {
      /* console.error('🔐 RBAC DEBUG - Error fetching user roles:', error); */
      /* console.log('🔐 RBAC DEBUG - Falling back to roles dropdown'); */
      // Fallback to roles dropdown
      try {
        const userDetails = await this.getUserDetails();
        const businessId = userDetails.data?.business_id || userDetails.business_id || 40;
        return await this.getRolesDropdown(businessId);
      } catch (fallbackError) {
        /* console.error('🔐 RBAC DEBUG - Fallback also failed:', fallbackError); */
        return { data: [] };
      }
    }
  }

  // Get roles dropdown
  async getRolesDropdown(businessId = null) {
    /* console.log('🔐 RBAC DEBUG - Fetching roles dropdown...'); */
    try {
      const token = await this.getAuthToken();
      /* console.log('🔐 RBAC DEBUG - Token obtained for roles dropdown:', token ? 'success' : 'failed'); */

      if (!token) {
        /* console.error('🔐 RBAC DEBUG - No token available for roles dropdown'); */
        return { data: [] };
      }

      const params = new URLSearchParams();
      if (businessId) {
        params.append('business_id', businessId);
        /* console.log('🔐 RBAC DEBUG - Business ID for roles:', businessId); */
      }

      const url = `${this.baseURL}/connector/api/roles-dropdown?${params.toString()}`;
      /* console.log('🔐 RBAC DEBUG - Roles dropdown URL:', url); */

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      /* console.log('🔐 RBAC DEBUG - Roles dropdown response status:', response.status); */
      /* console.log('🔐 RBAC DEBUG - Roles dropdown response ok:', response.ok); */

      if (!response.ok) {
        const errorText = await response.text();
        /* console.error('🔐 RBAC DEBUG - Roles dropdown error response:', errorText); */
        throw new Error(`Failed to fetch roles dropdown: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      /* console.log('🔐 RBAC DEBUG - Roles dropdown response data:', data); */
      /* console.log('🔐 RBAC DEBUG - Available roles count:', data.data ? data.data.length : (Array.isArray(data) ? data.length : 0)); */

      if (data.data) {
        data.data.forEach((role, index) => {
          /* console.log(`🔐 RBAC DEBUG - Role ${index + 1}:`, role); */
        });
      }
      /* console.log('🔐 RBAC DEBUG - getRolesDropdown completed successfully'); */
      return data;
    } catch (error) {
      /* console.error('🔐 RBAC DEBUG - Error fetching roles dropdown:', error); */
      /* console.log('🔐 RBAC DEBUG - Error details:', error.message, error.stack); */
      throw error;
    }
  }

  // Get roles from API
  async getRoles(businessId = null) {
    try {
      const token = await this.getAuthToken();
      const params = new URLSearchParams();
      if (businessId) {
        params.append('business_id', businessId);
      }
      const url = `${this.roleBaseURL}/connector/api/roles?${params.toString()}`;
      /* console.log('Fetching roles from URL:', url); */
      /* console.log('Using token:', token ? 'Token present' : 'No token'); */

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      /* console.log('Roles API response status:', response.status); */
      /* console.log('Roles API response content-type:', response.headers.get('content-type')); */

      if (!response.ok) {
        const errorText = await response.text();
        /* console.error('Roles API error response:', errorText); */
        throw new Error(`Failed to fetch roles: ${response.status} - ${errorText}`);
      }

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const htmlText = await response.text();
        /* console.error('Roles API returned HTML instead of JSON:', htmlText.substring(0, 500)); */
        throw new Error('API returned HTML instead of JSON. Check if the endpoint exists and is configured correctly.');
      }

      const data = await response.json();
      /* console.log('Roles API parsed data:', data); */
      return data;
    } catch (error) {
      /* console.error('Error fetching roles:', error); */
      throw error;
    }
  }

  // Get permission groups from API
  async getPermissionGroups(businessId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.roleBaseURL}/connector/api/permission-groups?business_id=${businessId}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch permission groups: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching permission groups:', error); */
      throw error;
    }
  }

  // Create a new role
  async createRole(roleData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.roleBaseURL}/connector/api/roles`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(roleData)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to create role: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error creating role:', error); */
      throw error;
    }
  }

  // Update an existing role
  async updateRole(roleId, roleData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.roleBaseURL}/connector/api/roles/${roleId}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(roleData)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to update role: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error updating role:', error); */
      throw error;
    }
  }

  // Delete a role
  async deleteRole(roleId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.roleBaseURL}/connector/api/roles/${roleId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to delete role: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting role:', error); */
      throw error;
    }
  }

  // Assign role to user
  async assignRoleToUser(userId, roleId, businessId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.roleBaseURL}/connector/api/users/${userId}/roles`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ business_id: businessId, role_id: roleId })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to assign role: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error assigning role to user:', error); */
      throw error;
    }
  }

  // Check user permission
  async checkUserPermission(userId, permission, businessId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.roleBaseURL}/connector/api/users/${userId}/check-permission`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ business_id: businessId, permission })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to check permission: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error checking user permission:', error); */
      throw error;
    }
  }

  // Notification Templates APIs
  async getNotificationTemplates() {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/notification-templates`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch notification templates: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching notification templates:', error); */
      throw error;
    }
  }

  async saveNotificationTemplates(templateData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/notification-templates`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ template_data: templateData })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to save notification templates: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error saving notification templates:', error); */
      throw error;
    }
  }

  async getNotificationTemplateTypes() {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/notification-templates/types`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch notification template types: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching notification template types:', error); */
      throw error;
    }
  }

  async getNotificationTemplate(templateFor) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/notification-templates/${encodeURIComponent(templateFor)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch notification template: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching notification template:', error); */
      throw error;
    }
  }

  // Get business locations from API (list for table)
  async getBusinessLocations() {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/business-location`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch business locations: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching business locations:', error); */
      throw error;
    }
  }

  // Get business location create data (dropdowns, etc.)
  async getBusinessLocationCreateData() {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/business-location/create/data`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.msg || `Failed to fetch business location create data: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching business location create data:', error); */
      throw error;
    }
  }

  // Create a new business location
  async createBusinessLocation(data) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/business-location`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const rawBody = await response.text();
        let errorText = rawBody;
        try {
          const parsed = JSON.parse(rawBody);
          if (parsed.message) {
            errorText = parsed.message;
          }
        } catch (e) { }
        throw new Error(`Failed to create business location: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error creating business location:', error); */
      throw error;
    }
  }

  // Get business location edit data (location + dropdowns)
  async getBusinessLocationEditData(id) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/business-location/${id}/edit-data`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch business location edit data: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching business location edit data:', error); */
      throw error;
    }
  }

  // Update a business location
  async updateBusinessLocation(id, data) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/business-location/${id}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const rawBody = await response.text();
        let errorText = rawBody;
        try {
          const parsed = JSON.parse(rawBody);
          if (parsed.message) {
            errorText = parsed.message;
          }
        } catch (e) { }
        throw new Error(`Failed to update business location: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error updating business location:', error); */
      throw error;
    }
  }

  // Check if location ID already exists
  async checkBusinessLocationId(locationId, hiddenId = null) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/business-location-check-location-id`;
      const body = { location_id: locationId };
      if (hiddenId) body.hidden_id = hiddenId;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(body)
      });
      if (!response.ok) throw new Error(`Failed to check location ID: ${response.status}`);
      const text = await response.text();
      return text === 'true';
    } catch (error) {
      /* console.error('Error checking location ID:', error); */
      throw error;
    }
  }

  // Activate or deactivate a business location
  async activateDeactivateBusinessLocation(locationId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/business-location/${locationId}/activate-deactivate`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to toggle business location: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error toggling business location:', error); */
      throw error;
    }
  }

  // Get location settings (receipt settings)
  async getLocationSettings(locationId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/business-location/${locationId}/settings`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch location settings: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching location settings:', error); */
      throw error;
    }
  }

  // Update location settings (receipt settings)
  async updateLocationSettings(locationId, data) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/business-location/${locationId}/settings`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const rawBody = await response.text();
        let errorText = rawBody;
        try {
          const parsed = JSON.parse(rawBody);
          if (parsed.message) {
            errorText = parsed.message;
          }
        } catch (e) { }
        throw new Error(`Failed to update location settings: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error updating location settings:', error); */
      throw error;
    }
  }

  // Import Opening Stock
  async importOpeningStock(fileData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/opening-stock/import`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: fileData
      });

      if (!response.ok) {
        const rawBody = await response.text();
        let errorText = rawBody;
        try {
          const parsed = JSON.parse(rawBody);
          if (parsed.errors && typeof parsed.errors === 'object') {
            const details = Object.entries(parsed.errors)
              .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
              .join(' | ');
            errorText = `${parsed.message || 'Validation failed'} → ${details}`;
          } else {
            errorText = parsed.message || rawBody;
          }
        } catch (e) { }
        throw new Error(`Failed to import opening stock: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error importing opening stock:', error); */
      throw error;
    }
  }

  // Download Opening Stock Template
  async downloadOpeningStockTemplate() {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/opening-stock/import-template`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error(`Failed to download template: ${response.status}`);
      return await response.blob();
    } catch (error) {
      /* console.error('Error downloading opening stock template:', error); */
      throw error;
    }
  }

  // Get existing opening stock for a product
  async getOpeningStock(productId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/products/${productId}/opening-stock`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch opening stock: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching opening stock:', error); */
      throw error;
    }
  }

  // Save/Edit opening stock for a product
  async saveOpeningStock(productId, stockData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/products/${productId}/opening-stock`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(stockData)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save opening stock: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error saving opening stock:', error); */
      throw error;
    }
  }

  // Get selling price groups from API (Paginated & Filtered)
  async getSellingPriceGroups(params = {}) {
    try {
      const token = await this.getAuthToken();
      let queryUrl = `${this.baseURL}/connector/api/selling-price-groups`;

      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.search) queryParams.append('search', params.search);
      if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);

      const queryString = queryParams.toString();
      if (queryString) {
        queryUrl += `?${queryString}`;
      }

      const response = await fetch(queryUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch selling price groups: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching selling price groups:', error); */
      throw error;
    }
  }

  // Get active selling price groups
  async getActiveSellingPriceGroups() {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/selling-price-groups/active`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch active selling price groups: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching active selling price groups:', error); */
      throw error;
    }
  }

  // Get specific selling price group
  async getSellingPriceGroup(id) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/selling-price-groups/${id}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch selling price group: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching selling price group:', error); */
      throw error;
    }
  }

  // Create selling price group
  async createSellingPriceGroup(data) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/selling-price-groups`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create selling price group: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error creating selling price group:', error); */
      throw error;
    }
  }

  // Update selling price group
  async updateSellingPriceGroup(id, data) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/selling-price-groups/${id}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update selling price group: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error updating selling price group:', error); */
      throw error;
    }
  }

  // Delete selling price group
  async deleteSellingPriceGroup(id) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/selling-price-groups/${id}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to delete selling price group: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting selling price group:', error); */
      throw error;
    }
  }

  // Activate/Deactivate Selling Price Group
  async activateDeactivateSellingPriceGroup(id, isActive) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/selling-price-groups/${id}/activate-deactivate`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: isActive })
      });
      if (!response.ok) throw new Error(`Failed to update selling price group status: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error updating selling price group status:', error); */
      throw error;
    }
  }

  // Toggle Selling Price Group Status
  async toggleSellingPriceGroupStatus(id) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/selling-price-groups/${id}/toggle`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to toggle selling price group status: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error toggling selling price group status:', error); */
      throw error;
    }
  }

  // Get locations for sell page
  async getLocations() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/business-location?per_page=-1`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch locations: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching locations:', error); */
      throw error;
    }
  }

  // Get sales commission agents from API
  async getSalesCommissionAgents() {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/sales-commission-agent?per_page=-1`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch agents: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching agents:', error); */
      throw error;
    }
  }

  // Create sales commission agent
  async createSalesCommissionAgent(agentData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/sales-commission-agent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(agentData)
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || `Failed to create agent: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error creating sales commission agent:', error); */
      throw error;
    }
  }

  // Update sales commission agent
  async updateSalesCommissionAgent(id, agentData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/sales-commission-agent/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(agentData)
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || `Failed to update agent: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error updating sales commission agent:', error); */
      throw error;
    }
  }

  // Delete sales commission agent
  async deleteSalesCommissionAgent(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/sales-commission-agent/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || `Failed to delete agent: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting sales commission agent:', error); */
      throw error;
    }
  }

  // Get specific category
  async getCategory(id) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/taxonomy/${id}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch category: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching category:', error); */
      throw error;
    }
  }

  // Create a new category
  async createCategory(categoryData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/taxonomy`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: categoryData.name,
          short_code: categoryData.short_code,
          category_type: categoryData.category_type || 'product',
          description: categoryData.description || undefined,
          parent_id: categoryData.parent_id || undefined,
          add_as_sub_cat: categoryData.add_as_sub_cat ? 1 : 0
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to create category: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error creating category:', error); */
      throw error;
    }
  }

  // Update an existing category
  async updateCategory(categoryId, categoryData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/taxonomy/${categoryId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: categoryData.name,
          short_code: categoryData.short_code,
          description: categoryData.description || undefined,
          parent_id: categoryData.parent_id || undefined,
          add_as_sub_cat: categoryData.add_as_sub_cat ? 1 : 0
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to update category: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error updating category:', error); */
      throw error;
    }
  }

  // Delete a category
  async deleteCategory(categoryId) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/taxonomy/${categoryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to delete category: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting category:', error); */
      throw error;
    }
  }
  // Get warranties
  async getWarranties() {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/warranties`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch warranties: ${response.status}`);
      }
      const data = await response.json();
      return data.data || data;
    } catch (error) {
      /* console.error('Error fetching warranties:', error); */
      throw error;
    }
  }

  // Create Warranty
  async createWarranty(warrantyData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/warranties`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(warrantyData)
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.msg || errorData.message || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Update Warranty
  async updateWarranty(id, warrantyData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/warranties/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(warrantyData)
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.msg || errorData.message || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Delete Warranty
  async deleteWarranty(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/warranties/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.msg || errorData.message || `HTTP ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  }
  // Get units
  async getUnits() {
    try {
      const token = await this.getAuthToken();
      // Using per_page=-1 to ensure we get all units
      const url = `${this.baseURL}/connector/api/unit?per_page=-1`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch units: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching units:', error); */
      throw error;
    }
  }

  // Get specific unit
  async getUnit(id) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/unit/${id}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch unit: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching unit:', error); */
      throw error;
    }
  }

  // Create a new unit
  async createUnit(unitData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/unit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          actual_name: unitData.actual_name,
          short_name: unitData.short_name,
          allow_decimal: unitData.allow_decimal ? 1 : 0,
          base_unit_id: unitData.base_unit_id || undefined,
          base_unit_multiplier: unitData.base_unit_multiplier || undefined
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to create unit: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error creating unit:', error); */
      throw error;
    }
  }

  // Update an existing unit
  async updateUnit(unitId, unitData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/unit/${unitId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          actual_name: unitData.actual_name,
          short_name: unitData.short_name,
          allow_decimal: unitData.allow_decimal ? 1 : 0,
          base_unit_id: unitData.base_unit_id || undefined,
          base_unit_multiplier: unitData.base_unit_multiplier || undefined
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to update unit: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error updating unit:', error); */
      throw error;
    }
  }

  // Delete a unit
  async deleteUnit(unitId) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/unit/${unitId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to delete unit: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting unit:', error); */
      throw error;
    }
  }
  // Get brands
  async getBrands() {
    try {
      const token = await this.getAuthToken();
      // Using per_page=-1 to ensure we get all brands
      const url = `${this.baseURL}/connector/api/brand?per_page=-1`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch brands: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching brands:', error); */
      throw error;
    }
  }

  // Get business details (name, logo, address, etc.)
  async getBusinessDetails() {
    try {
      const token = await this.getAuthToken();

      // PRIMARY: user/loggedin — always accessible regardless of role, eager-loads business.currency
      try {
        const userResponse = await fetch(`${this.baseURL}/connector/api/user/loggedin`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (userResponse.ok) {
          const userData = await userResponse.json();
          const bizObj = userData.data?.business || userData.data?.user?.business || userData.user?.business || userData.business;
          if (bizObj) {
            return { data: bizObj };
          }
        }
      } catch (e) {
        /* console.log('user/loggedin failed, trying business-settings fallback'); */
      }

      // FALLBACK: business-settings (admin only — requires business_settings.access permission)
      try {
        const response = await fetch(`${this.baseURL}/connector/api/business-settings`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          const bizObj = data.data?.business || data.data || data;
          if (bizObj) return { data: bizObj };
        }
      } catch (e) {
        /* console.log('business-settings endpoint also failed'); */
      }

      throw new Error('Failed to fetch business details from any endpoint');
    } catch (error) {
      /* console.error('Error fetching business details:', error); */
      throw error;
    }
  }

  // Update shipping details for a sale
  async updateShipping(saleId, shippingData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/update-shipping-status`;

      // If shippingData is FormData, append id to it (backend expects 'id' not 'sale_id')
      if (shippingData instanceof FormData) {
        shippingData.append('id', saleId);

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
            // Don't set Content-Type for FormData - let browser set it with boundary
          },
          body: shippingData
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to update shipping: ${response.status} - ${errorText}`);
        }

        const data = await response.json();
        return data;
      } else {
        // Handle JSON data - backend expects 'id' not 'sale_id'
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: saleId,
            ...shippingData
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to update shipping: ${response.status} - ${errorText}`);
        }

        const data = await response.json();
        return data;
      }
    } catch (error) {
      /* console.error('Error updating shipping:', error); */
      throw error;
    }
  }

  // Send sale notification (email or SMS)
  async sendSaleNotification(notificationData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/sell/${notificationData.sale_id}/notification`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(notificationData)
      });

      if (!response.ok) {
        throw new Error(`Failed to send notification: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error sending notification:', error); */
      throw error;
    }
  }

  // Send purchase notification (email or SMS)
  async sendPurchaseNotification(notificationData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/purchases/${notificationData.purchase_id}/notification`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(notificationData)
      });

      if (!response.ok) {
        throw new Error(`Failed to send notification: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error sending purchase notification:', error); */
      throw error;
    }
  }

  // ===== EXPENSE MANAGEMENT METHODS =====

  // Get expenses from API with filters
  async getExpenses(params = {}) {
    try {
      const token = await this.getAuthToken();

      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.search) queryParams.append('search', params.search);
      if (params.location_id && params.location_id !== 'all') queryParams.append('location_id', params.location_id);
      if (params.expense_category_id && params.expense_category_id !== 'all') queryParams.append('expense_category_id', params.expense_category_id);
      if (params.payment_status && params.payment_status !== 'all') queryParams.append('payment_status', params.payment_status);
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const url = `${this.baseURL}/connector/api/expense${queryString}`;
      /* console.log('[API getExpenses] URL:', url); */

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch expenses: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching expenses:', error); */
      throw error;
    }
  }

  // Create a new expense
  async createExpense(expenseData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/expense`;

      const formData = new FormData();
      Object.keys(expenseData).forEach(key => {
        if (key === 'document' && expenseData[key]) {
          formData.append(key, expenseData[key]);
        } else if (key === 'payment' && Array.isArray(expenseData[key])) {
          expenseData[key].forEach((paymentLine, idx) => {
            Object.keys(paymentLine).forEach(pKey => {
              if (paymentLine[pKey] !== null && paymentLine[pKey] !== undefined && paymentLine[pKey] !== '') {
                formData.append(`payment[${idx}][${pKey}]`, paymentLine[pKey]);
              }
            });
          });
        } else if (expenseData[key] !== null && expenseData[key] !== undefined) {
          formData.append(key, expenseData[key]);
        }
      });

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create expense: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error creating expense:', error); */
      throw error;
    }
  }

  // Update an existing expense
  async updateExpense(expenseId, expenseData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/expense/${expenseId}`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(expenseData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update expense: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error updating expense:', error); */
      throw error;
    }
  }

  // Delete an expense
  async deleteExpense(expenseId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/expense/${expenseId}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to delete expense: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error deleting expense:', error); */
      throw error;
    }
  }

  // Get expense categories
  async getExpenseCategories() {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/expense-categories?per_page=-1`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch expense categories: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error fetching expense categories:', error); */
      throw error;
    }
  }

  // Create a new expense category
  async createExpenseCategory(categoryData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/expense-categories`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(categoryData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create expense category: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error creating expense category:', error); */
      throw error;
    }
  }

  // Update an existing expense category
  async updateExpenseCategory(categoryId, categoryData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/expense-categories/${categoryId}`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(categoryData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update expense category: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error updating expense category:', error); */
      throw error;
    }
  }

  // Delete an expense category
  async deleteExpenseCategory(categoryId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/expense-categories/${categoryId}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to delete expense category: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error deleting expense category:', error); */
      throw error;
    }
  }

  // Create Plaid Link Token
  async createPlaidLinkToken() {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/plaid/create-link-token`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create Plaid link token: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      /* console.error('Error creating Plaid link token:', error); */
      throw error;
    }
  }

  // Exchange Plaid Public Token
  async exchangePlaidPublicToken(payload) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/plaid/exchange-public-token`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to exchange Plaid public token: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      /* console.error('Error exchanging Plaid public token:', error); */
      throw error;
    }
  }

  // Get Plaid Accounts
  async getPlaidAccounts() {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/plaid/accounts`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch Plaid accounts: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      /* console.error('Error fetching Plaid accounts:', error); */
      throw error;
    }
  }

  // Get current user profile
  async getCurrentUser() {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/user/loggedin`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch current user: ${response.status}`);
      }

      const data = await response.json();
      return data.data || data;
    } catch (error) {
      /* console.error('Error fetching current user:', error); */
      throw error;
    }
  }

  // Update user profile
  async updateUserProfile(profileData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/user/update-profile`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: profileData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update profile: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error updating profile:', error); */
      throw error;
    }
  }

  // Change password
  async changePassword(passwordData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/user/change-password`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword,
          new_password_confirmation: passwordData.confirmPassword
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to change password: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      /* console.error('Error changing password:', error); */
      throw error;
    }
  }

  // Delete a sell payment line
  async deleteSellPayment(paymentId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/sell-payment/${paymentId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete payment: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting sell payment:', error); */
      throw error;
    }
  }

  // Update a sell payment line
  async updateSellPayment(paymentId, paymentData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/sell-payment/${paymentId}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentData)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update payment: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error updating sell payment:', error); */
      throw error;
    }
  }

  // Get payments for a transaction (connector API endpoint)
  async getPayments(transactionId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/payment-history/${transactionId}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch payments: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching payments:', error); */
      throw error;
    }
  }

  // Get supplier stock report (connector API endpoint)
  async getSupplierStockReport(supplierId, locationId = null) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      if (locationId) queryParams.append('location_id', locationId);
      const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const url = `${this.baseURL}/connector/api/supplier-stock-report/${supplierId}${qs}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch stock report: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching supplier stock report:', error); */
      throw error;
    }
  }

  // Add payment to transaction (connector API endpoint)
  async addPayment(paymentData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/payment-methods/add`;
      /* console.log('Adding payment to transaction:', paymentData); */

      // Connector PaymentMethodController expects these exact field names
      const payload = {
        transaction_id: paymentData.transaction_id,
        amount: paymentData.amount,
        payment_method: paymentData.method,
        paid_on: paymentData.paid_on,
        payment_account_id: paymentData.payment_account_id || null,
        payment_note: paymentData.note || '',
        card_number: paymentData.card_number || '',
        card_holder_name: paymentData.card_holder_name || '',
        card_transaction_number: paymentData.card_transaction_number || '',
        card_type: paymentData.card_type || '',
        card_month: paymentData.card_month || '',
        card_year: paymentData.card_year || '',
        card_security: paymentData.card_security || '',
        cheque_number: paymentData.cheque_number || '',
        bank_account_number: paymentData.bank_account_number || ''
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to add payment: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      /* console.error('Error adding payment:', error); */
      throw error;
    }
  }

  // Get single sale details specifically for viewing (avoids /edit endpoint)
  async getSaleView(saleId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/sell/${saleId}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch sale view details: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      /* console.error('Error fetching sale view details:', error); */
      throw error;
    }
  }

  // Get single sale details (tries edit endpoint first for full product & payment relations)
  async getSale(saleId) {
    try {
      const token = await this.getAuthToken();
      let url = `${this.baseURL}/connector/api/sells/${saleId}/edit`;

      let response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        url = `${this.baseURL}/connector/api/sell/${saleId}`;
        response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch sale details: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      /* console.error('Error fetching sale details:', error); */
      throw error;
    }
  }

  // Get sales orders for customer and location
  async getSalesOrders(customerId, locationId) {
    try {
      const token = await this.getAuthToken();
      const webBaseURL = this.baseURL.replace('/connector/api', '');
      const response = await fetch(`${webBaseURL}/get-sales-orders/${customerId}?location_id=${locationId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch sales orders: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching sales orders:', error); */
      throw error;
    }
  }

  // Delete a sale
  async deleteSale(saleId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/sell/${saleId}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete sale: ${response.status} - ${errorText}`);
      }

      try {
        window.dispatchEvent(new Event('reload-subscription'));
      } catch (e) {
        /* console.error('Error dispatching reload-subscription on sale delete:', e); */
      }

      return await response.json();
    } catch (error) {
      /* console.error('Error deleting sale:', error); */
      throw error;
    }
  }

  // ── Stock Adjustment API Methods ──────────────────────────────────────────
  // GET /connector/api/stock-adjustment
  async getStockAdjustments(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      if (params.location_id) queryParams.append('location_id', params.location_id);
      if (params.search) queryParams.append('search', params.search);
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.page) queryParams.append('page', params.page);
      if (params.adjustment_type) queryParams.append('adjustment_type', params.adjustment_type);
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);

      const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${this.baseURL}/connector/api/stock-adjustment${qs}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch stock adjustments: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching stock adjustments:', error); */
      throw error;
    }
  }

  // POST /connector/api/stock-adjustment
  async createStockAdjustment(adjustmentData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/stock-adjustment`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(adjustmentData)
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to create stock adjustment: ${response.status}`;
        try {
          const parsed = JSON.parse(errorText);
          if (parsed.errors) errMsg = `${parsed.message}: ${Object.values(parsed.errors).flat().join(', ')}`;
          else errMsg = parsed.message || errMsg;
        } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error creating stock adjustment:', error); */
      throw error;
    }
  }

  // GET /connector/api/stock-adjustment/{id}
  async getStockAdjustmentDetails(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/stock-adjustment/${id}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch adjustment details: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching adjustment details:', error); */
      throw error;
    }
  }

  // DELETE /connector/api/stock-adjustment/{id}
  async deleteStockAdjustment(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/stock-adjustment/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to delete stock adjustment: ${response.status}`;
        try { const parsed = JSON.parse(errorText); errMsg = parsed.message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting stock adjustment:', error); */
      throw error;
    }
  }

  // ── Import / Export Product & Prices Endpoints ────────────────────────────
  async exportProductPrices() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/product-prices/export`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) {
        const rawBody = await response.text();
        let errorText = rawBody;
        try { errorText = JSON.parse(rawBody).message || rawBody; } catch (e) { }
        throw new Error(`${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error exporting prices:', error); */
      throw error;
    }
  }

  async downloadProductPrices(downloadUrl) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(downloadUrl, { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      return await response.blob();
    } catch (error) { throw error; }
  }

  async importProductPrices(fileData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/product-prices/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
        body: fileData
      });
      if (!response.ok) {
        const rawBody = await response.text();
        let errorText = rawBody;
        try { errorText = JSON.parse(rawBody).message || rawBody; } catch (e) { }
        throw new Error(`${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) { throw error; }
  }

  async getProductPriceGroups() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/product-prices/price-groups`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`${response.status}`);
      return await response.json();
    } catch (error) { throw error; }
  }

  async downloadImportProductTemplate() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/products/import-template`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`${response.status}`);
      return await response.blob();
    } catch (error) { throw error; }
  }

  async importProducts(fileData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/products/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
        body: fileData
      });
      if (!response.ok) {
        const rawBody = await response.text();
        let errorText = rawBody;
        try {
          const parsed = JSON.parse(rawBody);
          if (parsed.errors && typeof parsed.errors === 'object') {
            // Laravel validation errors: { field: ["msg1", "msg2"] }
            const details = Object.entries(parsed.errors)
              .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
              .join(' | ');
            errorText = `${parsed.message || 'Validation failed'} → ${details}`;
          } else {
            errorText = parsed.message || rawBody;
          }
        } catch (e) { }
        throw new Error(`${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) { throw error; }
  }

  // ── Brand API Methods ──────────────────────────────────────────────────────

  // GET /connector/api/brand  — list all brands
  async getBrands() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/brand`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch brands: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching brands:', error); */
      throw error;
    }
  }

  // GET /connector/api/brand/{id}  — get single or comma-separated brands
  async getBrand(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/brand/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch brand: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching brand:', error); */
      throw error;
    }
  }

  // POST /connector/api/brand  — create brand
  async createBrand(brandData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/brand`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: brandData.name,
          description: brandData.description || '',
          use_for_repair: brandData.use_for_repair ? 1 : 0
        })
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to create brand: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error creating brand:', error); */
      throw error;
    }
  }
  // PUT /connector/api/brand/{id}  — update brand
  async updateBrand(brandId, brandData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/brand/${brandId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: brandData.name,
          description: brandData.description || '',
          use_for_repair: brandData.use_for_repair ? 1 : 0
        })
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to update brand: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error updating brand:', error); */
      throw error;
    }
  }

  // DELETE /connector/api/brand/{id}  — delete brand
  async deleteBrand(brandId) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/brand/${brandId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to delete brand: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting brand:', error); */
      throw error;
    }
  }

  // ── Variation API Methods ──────────────────────────────────────────────────

  // GET /connector/api/variations — list all variations
  async getVariations(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.page) queryParams.append('page', params.page);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${this.baseURL}/connector/api/variations${queryString}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Failed to fetch variations: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching variations:', error); */
      throw error;
    }
  }

  // GET /connector/api/variations/{id} — get specific variation
  async getVariation(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/variations/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch variation: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching variation:', error); */
      throw error;
    }
  }

  // POST /connector/api/variations — create variation
  async createVariation(variationData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/variations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(variationData)
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to create variation: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error creating variation:', error); */
      throw error;
    }
  }

  // PUT /connector/api/variations/{id} — update variation
  async updateVariation(id, variationData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/variations/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(variationData)
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to update variation: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error updating variation:', error); */
      throw error;
    }
  }

  // DELETE /connector/api/variations/{id} — delete variation
  async deleteVariation(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/variations/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to delete variation: ${response.status}`;
        try {
          const parsed = JSON.parse(errorText);
          errMsg = parsed.message || errMsg;
        } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting variation:', error); */
      throw error;
    }
  }

  // DELETE /connector/api/variations/{id}/values/{value_id} — delete variation value
  async deleteVariationValue(id, valueId) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/variations/${id}/values/${valueId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to delete variation value: ${response.status}`;
        try {
          const parsed = JSON.parse(errorText);
          errMsg = parsed.message || errMsg;
        } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting variation value:', error); */
      throw error;
    }
  }

  // ── Stock Report API Methods ──────────────────────────────────────────────────

  // GET /connector/reports/stock/ — list stock report
  async getStockReport(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();

      // Filter parameters
      if (params.location_id) queryParams.append('location_id', params.location_id);
      if (params.category_id) queryParams.append('category_id', params.category_id);
      if (params.sub_category_id) queryParams.append('sub_category_id', params.sub_category_id);
      if (params.brand_id) queryParams.append('brand_id', params.brand_id);
      if (params.unit_id) queryParams.append('unit_id', params.unit_id);
      if (params.tax_id) queryParams.append('tax_id', params.tax_id);
      if (params.type) queryParams.append('type', params.type);
      if (params.active_state) queryParams.append('active_state', params.active_state);
      if (params.product_id) queryParams.append('product_id', params.product_id);
      if (params.supplier_id) queryParams.append('supplier_id', params.supplier_id);
      if (params.customer_id) queryParams.append('customer_id', params.customer_id);
      if (params.contact_id) queryParams.append('contact_id', params.contact_id);
      if (params.search) {
        queryParams.append('search', params.search);
        queryParams.append('sku', params.search);
        queryParams.append('name', params.search);
        queryParams.append('term', params.search);
      }
      if (params.sku && !params.search) queryParams.append('sku', params.sku);

      // Flags
      if (params.only_mfg_products) queryParams.append('only_mfg_products', '1');
      if (params.not_for_selling) queryParams.append('not_for_selling', '1');

      // Pagination
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.page) queryParams.append('page', params.page);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${this.baseURL}/connector/api/reports/stock${queryString}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Failed to fetch stock report: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching stock report:', error); */
      throw error;
    }
  }

  // GET /connector/api/reports/stock/filter-options — get filter options
  async getStockReportFilters() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/reports/stock/filter-options`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Failed to fetch stock report filters: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching stock report filters:', error); */
      throw error;
    }
  }

  // ── Purchase API Methods ──────────────────────────────────────────────────

  // GET /connector/api/purchasess — list all purchases
  async getPurchases(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();

      const allowedParams = [
        'location_id', 'supplier_id', 'status', 'payment_status',
        'start_date', 'end_date', 'per_page', 'page', 'search', 'business_id'
      ];

      allowedParams.forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${this.baseURL}/connector/api/purchases${queryString}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        /* console.error(`Purchase API Error (${response.status}):`, errorText); */
        throw new Error(`Failed to fetch purchases: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching purchases:', error); */
      throw error;
    }
  }

  // GET /connector/api/purchases/{id} — get specific purchase
  async getPurchase(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchases/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Failed to fetch purchase: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching purchase:', error); */
      throw error;
    }
  }

  // POST /connector/api/purchases — create purchase
  async createPurchase(purchaseData) {
    try {
      if (!navigator.onLine) {
        const { addToSyncQueue } = require('./db');
        await addToSyncQueue({
          action: 'CREATE_PURCHASE',
          data: purchaseData
        });
        /* console.log('Offline: Purchase queued for sync'); */
        return { success: true, message: 'Purchase saved offline. Will sync when internet is restored.' };
      }

      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchases`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(purchaseData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to create purchase: ${response.status}`;
        try {
          const parsed = JSON.parse(errorText);
          if (parsed.errors) {
            const details = Object.values(parsed.errors).flat().join(', ');
            errMsg = `${parsed.message}: ${details}`;
          } else {
            errMsg = parsed.message || errMsg;
          }
        } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error creating purchase:', error); */
      if (!navigator.onLine) {
        // Fallback catch if network drops during request
        const { addToSyncQueue } = require('./db');
        await addToSyncQueue({
          action: 'CREATE_PURCHASE',
          data: purchaseData
        });
        return { success: true, message: 'Purchase saved offline. Will sync when internet is restored.' };
      }
      throw error;
    }
  }

  // PUT /connector/api/purchases/{id} — update purchase
  async updatePurchase(id, purchaseData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchases/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(purchaseData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to update purchase: ${response.status}`;
        try {
          const parsed = JSON.parse(errorText);
          if (parsed.errors) {
            const details = Object.values(parsed.errors).flat().join(', ');
            errMsg = `${parsed.message}: ${details}`;
          } else {
            errMsg = parsed.message || errMsg;
          }
        } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error updating purchase:', error); */
      throw error;
    }
  }

  // DELETE /connector/api/purchases/{id} — delete purchase
  async deletePurchase(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchases/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to delete purchase: ${response.status}`;
        try {
          const parsed = JSON.parse(errorText);
          errMsg = parsed.message || errMsg;
        } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting purchase:', error); */
      throw error;
    }
  }

  // GET /connector/api/business-location — list business locations
  async getBusinessLocations(params = {}) {
    try {
      const token = await this.getAuthToken();
      const query = new URLSearchParams(params).toString();
      const response = await fetch(`${this.baseURL}/connector/api/business-location${query ? '?' + query : ''}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch locations: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching business locations:', error); */
      throw error;
    }
  }

  // PUT /connector/api/stock-transfer/{id} — update a stock transfer
  async updateStockTransfer(id, payload) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/stock-transfer/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to update stock transfer: ${response.status}`;
        try {
          const parsed = JSON.parse(errorText);
          errMsg = parsed.message || errMsg;
        } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error updating stock transfer:', error); */
      throw error;
    }
  }

  // DELETE /connector/api/stock-transfer/{id} — delete a stock transfer
  async deleteStockTransfer(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/stock-transfer/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to delete stock transfer: ${response.status}`;
        try {
          const parsed = JSON.parse(errorText);
          errMsg = parsed.message || errMsg;
        } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting stock transfer:', error); */
      throw error;
    }
  }

  // PATCH /connector/api/stock-transfer/{id}/status — update status only
  async updateStockTransferStatus(id, status) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/stock-transfer/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ status })
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to update stock transfer status: ${response.status}`;
        try {
          const parsed = JSON.parse(errorText);
          errMsg = parsed.message || errMsg;
        } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error updating stock transfer status:', error); */
      throw error;
    }
  }

  // GET /connector/api/purchases/statistics — get purchase statistics
  async getPurchaseStatistics(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.location_id) queryParams.append('location_id', params.location_id);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${this.baseURL}/connector/api/purchases/statistics${queryString}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Failed to fetch purchase statistics: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching purchase statistics:', error); */
      throw error;
    }
  }

  // ── Stock Transfer API Methods ─────────────────────────────────────────────
  // GET /connector/api/stock-transfer
  async getStockTransfers(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.page) queryParams.append('page', params.page);
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.location_id) queryParams.append('location_id', params.location_id);
      if (params.status) queryParams.append('status', params.status);
      if (params.search) queryParams.append('search', params.search);

      const qs = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${this.baseURL}/connector/api/stock-transfer${qs}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch stock transfers: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching stock transfers:', error); */
      throw error;
    }
  }

  // GET /connector/api/stock-transfer/{id}
  async getStockTransfer(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/stock-transfer/${id}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch stock transfer: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching stock transfer:', error); */
      throw error;
    }
  }

  // POST /connector/api/stock-transfer
  async createStockTransfer(payload) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/stock-transfer`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to create stock transfer: ${response.status}`;
        try {
          const parsed = JSON.parse(errorText);
          if (parsed.errors) errMsg = `${parsed.message}: ${Object.values(parsed.errors).flat().join(', ')}`;
          else errMsg = parsed.message || errMsg;
        } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error creating stock transfer:', error); */
      throw error;
    }
  }

  // PUT /connector/api/stock-transfer/{id}
  async updateStockTransfer(id, payload) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/stock-transfer/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to update stock transfer: ${response.status}`;
        try { const parsed = JSON.parse(errorText); errMsg = parsed.message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error updating stock transfer:', error); */
      throw error;
    }
  }

  // DELETE /connector/api/stock-transfer/{id}
  async deleteStockTransfer(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/stock-transfer/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to delete stock transfer: ${response.status}`;
        try { const parsed = JSON.parse(errorText); errMsg = parsed.message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting stock transfer:', error); */
      throw error;
    }
  }

  // PATCH /connector/api/stock-transfer/{id}/status
  async updateStockTransferStatus(id, status) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/stock-transfer/${id}/status`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to update stock transfer status: ${response.status}`;
        try { const parsed = JSON.parse(errorText); errMsg = parsed.message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error updating stock transfer status:', error); */
      throw error;
    }
  }

  // GET /connector/api/reports/stock
  async getStockReport(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) queryParams.append(k, v); });
      const url = `${this.baseURL}/connector/api/reports/stock?${queryParams.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch stock report: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching stock report:', error); */
      throw error;
    }
  }

  // GET /connector/api/reports/stock/filter-options
  async getStockReportFilters() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/reports/stock/filter-options`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch stock report filters: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching stock report filters:', error); */
      throw error;
    }
  }

  // GET /connector/api/products/{product_id}/stock-history/details
  async getProductForStockHistory(productId) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/products/${productId}/stock-history/details`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch product details: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching product for stock history:', error); */
      throw error;
    }
  }

  // GET /connector/api/products/{product_id}/stock-history?variation_id=X&location_id=Y
  async getProductStockHistory(productId, variationId, locationId) {
    try {
      const token = await this.getAuthToken();
      const params = new URLSearchParams({ variation_id: variationId, location_id: locationId });
      const response = await fetch(`${this.baseURL}/connector/api/products/${productId}/stock-history?${params}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch stock history: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching product stock history:', error); */
      throw error;
    }
  }

  // ── Manufacturing API Methods ─────────────────────────────────────────────
  // GET /connector/api/manufacturing/recipes
  async getManufacturingRecipes() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/manufacturing/recipes`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch recipes: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching manufacturing recipes:', error); */
      throw error;
    }
  }

  // DELETE /connector/api/manufacturing/recipes/{id}
  async deleteManufacturingRecipe(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/manufacturing/recipes/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to delete recipe: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting recipe:', error); */
      throw error;
    }
  }

  // GET /connector/api/manufacturing/productions
  async getManufacturingProductions(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) queryParams.append(k, v); });
      const url = `${this.baseURL}/connector/api/manufacturing/productions?${queryParams.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch productions: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching manufacturing productions:', error); */
      throw error;
    }
  }

  // DELETE /connector/api/manufacturing/productions/{id}
  async deleteManufacturingProduction(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/manufacturing/productions/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to delete production: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting production:', error); */
      throw error;
    }
  }

  // GET /connector/api/manufacturing/settings
  async getManufacturingSettings() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/manufacturing/settings`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch manufacturing settings: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching manufacturing settings:', error); */
      throw error;
    }
  }

  // POST /connector/api/manufacturing/settings
  async saveManufacturingSettings(payload) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/manufacturing/settings`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(`Failed to save manufacturing settings: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error saving manufacturing settings:', error); */
      throw error;
    }
  }

  // GET /connector/api/manufacturing/report
  async getManufacturingReport(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) queryParams.append(k, v); });
      const url = `${this.baseURL}/connector/api/manufacturing/report?${queryParams.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch manufacturing report: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching manufacturing report:', error); */
      throw error;
    }
  }

  // POST /connector/api/manufacturing/update-product-prices
  async updateManufacturingProductPrices(payload) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/manufacturing/update-product-prices`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(`Failed to update product prices: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error updating manufacturing product prices:', error); */
      throw error;
    }
  }

  // ── Repair API Methods ─────────────────────────────────────────────
  async getRepairDashboard() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/repair/dashboard`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/repair/dashboard`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { job_sheets_by_status: [], trending_brands: [], trending_devices: [] });
    } catch (error) { return { job_sheets_by_status: [], trending_brands: [], trending_devices: [] }; }
  }

  async getRepairJobSheets(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) queryParams.append(k, v); });
      let response = await fetch(`${this.baseURL}/connector/api/repair/job-sheets?${queryParams.toString()}`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/repair/job-sheets?${queryParams.toString()}`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { data: [] });
    } catch (error) { return { data: [] }; }
  }

  async getRepairRepairs(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) queryParams.append(k, v); });
      let response = await fetch(`${this.baseURL}/connector/api/repair/repairs?${queryParams.toString()}`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/repair/repairs?${queryParams.toString()}`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { data: [] });
    } catch (error) { return { data: [] }; }
  }

  async getRepairStatuses() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/repair/statuses`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/repair/statuses`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { data: [] });
    } catch (error) { return { data: [] }; }
  }

  async getRepairDeviceModels() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/repair/device-models`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/repair/device-models`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { data: [] });
    } catch (error) { return { data: [] }; }
  }

  async getRepairBrands() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/repair/brands`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/repair/brands`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { data: [] });
    } catch (error) { return { data: [] }; }
  }

  async getRepairDevices() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/repair/devices`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/repair/devices`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { data: [] });
    } catch (error) { return { data: [] }; }
  }

  async getRepairSettings() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/settings`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch repair settings: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching repair settings:', error); */ throw error; }
  }

  async saveRepairSettings(payload) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/settings`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(`Failed to save repair settings: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error saving repair settings:', error); */ throw error; }
  }

  // Project API methods
  async getProjectProjects(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) queryParams.append(k, v); });
      const url = `${this.baseURL}/connector/api/project/projects?${queryParams.toString()}`;
      const response = await fetch(url, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch projects: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching projects:', error); */ throw error; }
  }

  async createProjectProject(payload) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/projects`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(`Failed to create project: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error creating project:', error); */ throw error; }
  }

  async updateProjectProject(id, payload) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/projects/${id}`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(`Failed to update project: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error updating project:', error); */ throw error; }
  }

  async deleteProjectProject(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/projects/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to delete project: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error deleting project:', error); */ throw error; }
  }

  async getProjectTasks(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) queryParams.append(k, v); });
      const url = `${this.baseURL}/connector/api/project/tasks?${queryParams.toString()}`;
      const response = await fetch(url, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch tasks: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching tasks:', error); */ throw error; }
  }

  async createProjectTask(payload) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/tasks`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(`Failed to create task: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error creating task:', error); */ throw error; }
  }

  async updateProjectTask(id, payload) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/tasks/${id}`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(`Failed to update task: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error updating task:', error); */ throw error; }
  }

  async deleteProjectTask(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/tasks/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to delete task: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error deleting task:', error); */ throw error; }
  }

  async getProjectStatuses() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/statuses`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch project statuses: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching project statuses:', error); */ throw error; }
  }

  async getProjectCategories() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/categories`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch project categories: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching project categories:', error); */ throw error; }
  }

  async createProjectCategory(payload) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/categories`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(`Failed to create project category: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error creating project category:', error); */ throw error; }
  }

  async updateProjectCategory(id, payload) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/categories/${id}`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(`Failed to update project category: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error updating project category:', error); */ throw error; }
  }

  async deleteProjectCategory(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/categories/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to delete project category: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error deleting project category:', error); */ throw error; }
  }

  async getProjectSettings() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/settings`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch project settings: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching project settings:', error); */ throw error; }
  }

  async saveProjectSettings(payload) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/settings`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(`Failed to save project settings: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error saving project settings:', error); */ throw error; }
  }

  async getProjectUsers() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/users`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch project users: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching project users:', error); */ throw error; }
  }

  async getProjectCustomers() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/customers`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch project customers: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching project customers:', error); */ throw error; }
  }

  async getProjectEmployeeTimeLogReport(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) queryParams.append(k, v); });
      const url = `${this.baseURL}/connector/api/project/reports/employee-timelog?${queryParams.toString()}`;
      const response = await fetch(url, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch employee time log report: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching employee time log report:', error); */ throw error; }
  }

  async getProjectProjectTimeLogReport(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) queryParams.append(k, v); });
      const url = `${this.baseURL}/connector/api/project/reports/project-timelog?${queryParams.toString()}`;
      const response = await fetch(url, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch project time log report: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching project time log report:', error); */ throw error; }
  }



  // HRM APIs
  async getHrmDashboard() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/dashboard`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch HRM dashboard: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching HRM dashboard:', error); */ throw error; }
  }

  async getHrmLeaveTypes() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/leave-types`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch leave types: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching leave types:', error); */ throw error; }
  }

  async createHrmLeaveType(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/leave-types`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to create leave type: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error creating leave type:', error); */ throw error; }
  }

  async updateHrmLeaveType(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/leave-types/${id}`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to update leave type: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error updating leave type:', error); */ throw error; }
  }

  async deleteHrmLeaveType(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/leave-types/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to delete leave type: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error deleting leave type:', error); */ throw error; }
  }

  async getHrmLeaves(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) queryParams.append(k, v); });
      const url = `${this.baseURL}/connector/api/hrm/leaves?${queryParams.toString()}`;
      const response = await fetch(url, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch leaves: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching leaves:', error); */ throw error; }
  }

  async createHrmLeave(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/leaves`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to create leave: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error creating leave:', error); */ throw error; }
  }

  async updateHrmLeaveStatus(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/leaves/${id}/status`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to update leave status: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error updating leave status:', error); */ throw error; }
  }

  async getHrmAttendance(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) queryParams.append(k, v); });
      const url = `${this.baseURL}/connector/api/hrm/attendance?${queryParams.toString()}`;
      const response = await fetch(url, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch attendance: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching attendance:', error); */ throw error; }
  }

  async getHrmShifts() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/shifts`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch shifts: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching shifts:', error); */ throw error; }
  }

  async getHrmHolidays(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v !== '' && v !== null && v !== undefined) queryParams.append(k, v); });
      const url = `${this.baseURL}/connector/api/hrm/holidays?${queryParams.toString()}`;
      const response = await fetch(url, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch holidays: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching holidays:', error); */ throw error; }
  }

  async createHrmHoliday(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/holidays`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to create holiday: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error creating holiday:', error); */ throw error; }
  }

  async updateHrmHoliday(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/holidays/${id}`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to update holiday: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error updating holiday:', error); */ throw error; }
  }

  async deleteHrmHoliday(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/holidays/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to delete holiday: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error deleting holiday:', error); */ throw error; }
  }

  async getHrmPayrolls() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/payrolls`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch payrolls: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching payrolls:', error); */ throw error; }
  }

  async createHrmPayroll(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/payrolls`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      if (!response.ok) throw new Error(`Failed to create payroll: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error creating payroll:', error); */ throw error; }
  }

  async updateHrmPayroll(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/payrolls/${id}`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      if (!response.ok) throw new Error(`Failed to update payroll: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error updating payroll:', error); */ throw error; }
  }

  async getHrmPayrollGroups() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/payroll-groups`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch payroll groups: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching payroll groups:', error); */ throw error; }
  }

  async getHrmSalesTargets() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/sales-targets`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch sales targets: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching sales targets:', error); */ throw error; }
  }

  async getHrmSettings() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/settings`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch HRM settings: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching HRM settings:', error); */ throw error; }
  }

  async updateHrmSettings(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/settings`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to update HRM settings: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error updating HRM settings:', error); */ throw error; }
  }

  async getHrmDepartments() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/departments`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch departments: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching departments:', error); */ throw error; }
  }

  async createHrmDepartment(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/departments`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      if (!response.ok) throw new Error(`Failed to create department: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error creating department:', error); */ throw error; }
  }

  async updateHrmDepartment(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/departments/${id}`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      if (!response.ok) throw new Error(`Failed to update department: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error updating department:', error); */ throw error; }
  }

  async deleteHrmDepartment(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/departments/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to delete department: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error deleting department:', error); */ throw error; }
  }

  async getHrmDesignations() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/designations`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch designations: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching designations:', error); */ throw error; }
  }

  async createHrmDesignation(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/designations`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      if (!response.ok) throw new Error(`Failed to create designation: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error creating designation:', error); */ throw error; }
  }

  async updateHrmDesignation(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/designations/${id}`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      if (!response.ok) throw new Error(`Failed to update designation: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error updating designation:', error); */ throw error; }
  }

  async deleteHrmDesignation(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/designations/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to delete designation: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error deleting designation:', error); */ throw error; }
  }

  async getHrmUsers() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/users`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch HRM users: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching HRM users:', error); */ throw error; }
  }

  async getHrmLocations() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/locations`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch HRM locations: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching HRM locations:', error); */ throw error; }
  }

  async createHrmAttendance(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/attendance`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to create attendance: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error creating attendance:', error); */ throw error; }
  }

  async deleteHrmAttendance(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/attendance/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to delete attendance: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error deleting attendance:', error); */ throw error; }
  }

  async clockInClockOut(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/hrm/clock-in-clock-out`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to clock in/out: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error in clockInClockOut:', error); */ throw error; }
  }

  async getUserLeaveSummary(userId, startDate, endDate) {
    try {
      const token = await this.getAuthToken();
      const params = new URLSearchParams();
      if (userId) params.append('user_id', userId);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const response = await fetch(`${this.baseURL}/connector/api/hrm/user-leave-summary?${params.toString()}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch leave summary: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error in getUserLeaveSummary:', error); */ throw error; }
  }

  async getUserAttendanceSummary(userId, startDate, endDate) {
    try {
      const token = await this.getAuthToken();
      const params = new URLSearchParams();
      if (userId) params.append('user_id', userId);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      const response = await fetch(`${this.baseURL}/connector/api/hrm/user-attendance-summary?${params.toString()}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch attendance summary: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error in getUserAttendanceSummary:', error); */ throw error; }
  }

  // Business Settings APIs
  async getBusinessSettings() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/business-settings`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch business settings: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching business settings:', error); */ throw error; }
  }

  async updateBusinessSettings(data) {
    try {
      const token = await this.getAuthToken();
      const isFormData = data instanceof FormData;
      const headers = { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' };
      if (!isFormData) {
        headers['Content-Type'] = 'application/json';
      }

      const response = await fetch(`${this.baseURL}/connector/api/business-settings`, {
        method: 'POST',
        headers: headers,
        body: isFormData ? data : JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to update business settings: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error updating business settings:', error); */ throw error; }
  }

  async testEmailConfiguration(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/test-email`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to test email: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error testing email:', error); */ throw error; }
  }

  async testSmsConfiguration(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/test-sms`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to test SMS: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error testing SMS:', error); */ throw error; }
  }

  // CRM Dashboard API
  async getCrmDashboard() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/dashboard`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch CRM dashboard: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching CRM dashboard:', error); */ throw error; }
  }

  // CRM Leads API
  async getCrmLeads(params = {}) {
    try {
      const token = await this.getAuthToken();
      const query = new URLSearchParams();
      if (params.per_page) query.append('per_page', params.per_page);
      const queryString = query.toString() ? `?${query.toString()}` : '';
      const response = await fetch(`${this.baseURL}/api/crm/leads${queryString}`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch CRM leads: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching CRM leads:', error); */ throw error; }
  }

  async getCrmLeadCreateResources() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/leads/create`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch CRM lead resources: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching CRM lead resources:', error); */ throw error; }
  }

  async createCrmLead(leadData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/leads`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(leadData)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create CRM lead: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) { /* console.error('Error creating CRM lead:', error); */ throw error; }
  }

  async getCrmLeadDetails(id) {
    if (!id || String(id) === 'undefined') throw new Error('Invalid lead ID');
    try {
      const token = await this.getAuthToken();
      let response;
      try {
        response = await fetch(`${this.baseURL}/api/crm/leads/${id}`, {
          method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        });
      } catch (e) { response = null; }
      if (!response || !response.ok) {
        response = await fetch(`${this.baseURL}/crm/leads/${id}`, {
          method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        });
      }
      if (!response.ok) throw new Error(`Failed to fetch CRM lead details: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching CRM lead details:', error); */ throw error; }
  }

  async getCrmLeadEdit(id) {
    if (!id || String(id) === 'undefined') throw new Error('Invalid lead ID');
    try {
      const token = await this.getAuthToken();
      let response;
      try {
        response = await fetch(`${this.baseURL}/api/crm/leads/${id}/edit`, {
          method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        });
      } catch (e) { response = null; }
      if (!response || !response.ok) {
        response = await fetch(`${this.baseURL}/crm/leads/${id}/edit`, {
          method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        });
      }
      if (!response.ok) throw new Error(`Failed to fetch CRM lead for edit: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching CRM lead for edit:', error); */ throw error; }
  }

  async updateCrmLead(id, leadData) {
    try {
      const token = await this.getAuthToken();
      let response;
      try {
        response = await fetch(`${this.baseURL}/api/crm/leads/${id}`, {
          method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(leadData)
        });
      } catch (e) { response = null; }
      if (!response || !response.ok) {
        response = await fetch(`${this.baseURL}/crm/leads/${id}`, {
          method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(leadData)
        });
      }
      if (!response.ok) throw new Error(`Failed to update CRM lead: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error updating CRM lead:', error); */ throw error; }
  }

  async deleteCrmLead(id) {
    if (!id || String(id) === 'undefined') throw new Error('Invalid lead ID');
    try {
      const token = await this.getAuthToken();
      let response;
      try {
        response = await fetch(`${this.baseURL}/api/crm/leads/${id}`, {
          method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        });
      } catch (e) { response = null; }
      if (!response || !response.ok) {
        response = await fetch(`${this.baseURL}/crm/leads/${id}`, {
          method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        });
      }
      if (!response.ok) throw new Error(`Failed to delete CRM lead: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error deleting CRM lead:', error); */ throw error; }
  }

  async convertLeadToCustomer(id) {
    if (!id || String(id) === 'undefined') throw new Error('Invalid lead ID');
    try {
      const token = await this.getAuthToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      };
      let response;
      try {
        response = await fetch(`${this.baseURL}/crm/lead/${id}/convert`, {
          method: 'GET', headers
        });
      } catch (e) { response = null; }
      if (!response || !response.ok) {
        response = await fetch(`${this.baseURL}/api/crm/lead/${id}/convert`, {
          method: 'GET', headers
        });
      }
      if (!response.ok) throw new Error(`Failed to convert lead to customer: ${response.status}`);
      const data = await response.json();
      if (data && data.success === false) {
        throw new Error(data.msg || 'Failed to convert lead');
      }
      return data;
    } catch (error) { /* console.error('Error converting lead:', error); */ throw error; }
  }

  async updateLeadLifeStage(id, crmLifeStage) {
    try {
      const token = await this.getAuthToken();
      let response;
      try {
        response = await fetch(`${this.baseURL}/api/crm/lead/${id}/post-life-stage?crm_life_stage=${crmLifeStage}`, {
          method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
        });
      } catch (e) { response = null; }
      if (!response || !response.ok) {
        response = await fetch(`${this.baseURL}/crm/lead/${id}/post-life-stage?crm_life_stage=${crmLifeStage}`, {
          method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
        });
      }
      if (!response.ok) throw new Error(`Failed to update lead life stage: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error updating life stage:', error); */ throw error; }
  }

  // CRM Follow-ups API
  getCsrfToken() {
    if (typeof document === 'undefined') return '';
    const meta = document.querySelector('meta[name="csrf-token"]');
    if (meta) return meta.getAttribute('content');
    const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
    if (match) return decodeURIComponent(match[1]);
    return window.Laravel?.csrfToken || '';
  }

  async getCrmFollowUps(params = {}) {
    try {
      const token = await this.getAuthToken();
      const query = new URLSearchParams();
      Object.keys(params || {}).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          query.append(key, params[key]);
        }
      });
      const queryString = query.toString() ? `?${query.toString()}` : '';
      const csrf = this.getCsrfToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      };
      if (csrf) headers['X-CSRF-TOKEN'] = csrf;

      let response;
      try {
        response = await fetch(`${this.baseURL}/api/crm/follow-ups${queryString}`, { method: 'GET', headers });
      } catch (e) { response = null; }
      if (!response || !response.ok) {
        response = await fetch(`${this.baseURL}/crm/follow-ups${queryString}`, { method: 'GET', headers });
      }
      if (!response.ok) throw new Error(`Failed to fetch CRM follow-ups: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching CRM follow-ups:', error); */ throw error; }
  }

  async getCrmFollowUpResources() {
    try {
      const token = await this.getAuthToken();
      const csrf = this.getCsrfToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      };
      if (csrf) headers['X-CSRF-TOKEN'] = csrf;

      let response;
      try {
        response = await fetch(`${this.baseURL}/api/crm/follow-ups/create`, { method: 'GET', headers });
      } catch (e) { response = null; }
      if (!response || !response.ok) {
        response = await fetch(`${this.baseURL}/crm/follow-ups/create`, { method: 'GET', headers });
      }
      if (!response.ok) throw new Error(`Failed to fetch CRM follow-up resources: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching CRM follow-up resources:', error); */ throw error; }
  }

  async createCrmFollowUp(data) {
    try {
      const token = await this.getAuthToken();
      const csrf = this.getCsrfToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      };
      if (csrf) headers['X-CSRF-TOKEN'] = csrf;

      let response;
      try {
        response = await fetch(`${this.baseURL}/api/crm/follow-ups`, {
          method: 'POST', headers, body: JSON.stringify(data)
        });
      } catch (e) { response = null; }
      if (!response || !response.ok) {
        response = await fetch(`${this.baseURL}/crm/follow-ups`, {
          method: 'POST', headers, body: JSON.stringify(data)
        });
      }
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create CRM follow-up: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) { /* console.error('Error creating CRM follow-up:', error); */ throw error; }
  }

  async getCrmFollowUpLog(scheduleId) {
    try {
      const token = await this.getAuthToken();
      const csrf = this.getCsrfToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      };
      if (csrf) headers['X-CSRF-TOKEN'] = csrf;

      let response;
      try {
        response = await fetch(`${this.baseURL}/api/crm/follow-up-log?schedule_id=${scheduleId}&modal_content=true`, { method: 'GET', headers });
      } catch (e) { response = null; }
      if (!response || !response.ok) {
        response = await fetch(`${this.baseURL}/crm/follow-up-log?schedule_id=${scheduleId}&modal_content=true`, { method: 'GET', headers });
      }
      if (!response.ok) throw new Error(`Failed to fetch follow up log: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching follow up log:', error); */ throw error; }
  }

  async createCrmFollowUpLog(data) {
    try {
      const token = await this.getAuthToken();
      const csrf = this.getCsrfToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      };
      if (csrf) headers['X-CSRF-TOKEN'] = csrf;

      let response;
      try {
        response = await fetch(`${this.baseURL}/api/crm/follow-up-log`, {
          method: 'POST', headers, body: JSON.stringify(data)
        });
      } catch (e) { response = null; }
      if (!response || !response.ok) {
        response = await fetch(`${this.baseURL}/crm/follow-up-log`, {
          method: 'POST', headers, body: JSON.stringify(data)
        });
      }
      if (!response.ok) throw new Error(`Failed to create follow up log: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error creating follow up log:', error); */ throw error; }
  }

  async getCrmFollowUpEdit(id) {
    try {
      const token = await this.getAuthToken();
      const csrf = this.getCsrfToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json, text/html',
        'X-Requested-With': 'XMLHttpRequest'
      };
      if (csrf) headers['X-CSRF-TOKEN'] = csrf;

      let response;
      try {
        response = await fetch(`${this.baseURL}/api/crm/follow-ups/${id}/edit`, { method: 'GET', headers });
      } catch (e) { response = null; }
      if (!response || !response.ok) {
        response = await fetch(`${this.baseURL}/crm/follow-ups/${id}/edit`, { method: 'GET', headers });
      }
      if (!response.ok) throw new Error(`Failed to fetch follow-up edit data: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching follow-up edit:', error); */ throw error; }
  }

  async updateCrmFollowUp(id, data) {
    try {
      const token = await this.getAuthToken();
      const csrf = this.getCsrfToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      };
      if (csrf) headers['X-CSRF-TOKEN'] = csrf;

      let response;
      try {
        response = await fetch(`${this.baseURL}/api/crm/follow-ups/${id}`, {
          method: 'PUT', headers, body: JSON.stringify(data)
        });
      } catch (e) { response = null; }
      if (!response || !response.ok) {
        response = await fetch(`${this.baseURL}/crm/follow-ups/${id}`, {
          method: 'PUT', headers, body: JSON.stringify(data)
        });
      }
      if (!response.ok) throw new Error(`Failed to update follow-up: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error updating follow-up:', error); */ throw error; }
  }

  async deleteCrmFollowUp(id) {
    try {
      const token = await this.getAuthToken();
      const csrf = this.getCsrfToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      };
      if (csrf) headers['X-CSRF-TOKEN'] = csrf;

      let response;
      try {
        response = await fetch(`${this.baseURL}/api/crm/follow-ups/${id}`, { method: 'DELETE', headers });
      } catch (e) { response = null; }
      if (!response || !response.ok) {
        response = await fetch(`${this.baseURL}/crm/follow-ups/${id}`, { method: 'DELETE', headers });
      }
      if (!response.ok) throw new Error(`Failed to delete follow-up: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error deleting follow-up:', error); */ throw error; }
  }

  // CRM Campaigns API
  async getCrmCampaigns(params = {}) {
    try {
      const token = await this.getAuthToken();
      const query = new URLSearchParams();
      if (params.per_page) query.append('per_page', params.per_page);
      if (params.campaign_type) query.append('campaign_type', params.campaign_type);
      const queryString = query.toString() ? `?${query.toString()}` : '';
      const response = await fetch(`${this.baseURL}/api/crm/campaigns${queryString}`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch CRM campaigns: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching CRM campaigns:', error); */ throw error; }
  }

  async createCrmCampaign(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/campaigns`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create CRM campaign: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) { /* console.error('Error creating CRM campaign:', error); */ throw error; }
  }

  async updateCrmCampaign(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/campaigns/${id}`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to update campaign: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error updating campaign:', error); */ throw error; }
  }

  async deleteCrmCampaign(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/campaigns/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to delete campaign: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error deleting campaign:', error); */ throw error; }
  }

  async sendCampaignNotification(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/campaign/${id}/send-notification`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to send campaign notification: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error sending campaign notification:', error); */ throw error; }
  }

  async getLoggedInUser() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/user/loggedin`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch user loggedin: ${response.status}`);
      const data = await response.json();
      const biz = data?.data?.business || data?.data?.user?.business || data?.business;
      if (biz) {
        if (biz.date_format) localStorage.setItem('etpos_date_format', biz.date_format);
        if (biz.time_format) localStorage.setItem('etpos_time_format', String(biz.time_format));
      }
      return data;
    } catch (error) { /* console.error('Error fetching loggedin user:', error); */ throw error; }
  }

  // Category API (for product categories, CRM sources, life stages, followup categories)
  async getCategories(type = 'product') {
    try {
      const token = await this.getAuthToken();
      const categoryType = type || 'product';
      const response = await fetch(`${this.baseURL}/connector/api/taxonomy?type=${encodeURIComponent(categoryType)}`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch categories: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching categories:', error); */ throw error; }
  }

  async createCategory(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/taxonomy`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create category: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) { /* console.error('Error creating category:', error); */ throw error; }
  }

  async updateCategory(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/taxonomy/${id}`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to update category: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error updating category:', error); */ throw error; }
  }

  async deleteCategory(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/taxonomy/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to delete category: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error deleting category:', error); */ throw error; }
  }

  // CRM Settings API
  async getCrmSettings() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/settings`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch CRM settings: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching CRM settings:', error); */ throw error; }
  }

  async updateCrmSettings(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/update-settings`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update CRM settings: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) { /* console.error('Error updating CRM settings:', error); */ throw error; }
  }

  async getCrmContactsLogin() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/api/crm/contact-login`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) {
        response = await fetch(`${this.baseURL}/crm/contact-login`, {
          method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
        });
      }
      if (!response.ok) throw new Error(`Failed to fetch contacts login: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching contacts login:', error); */ throw error; }
  }

  async createCrmContactLogin(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/contact-login`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create contact login: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) { /* console.error('Error creating contact login:', error); */ throw error; }
  }

  async updateCrmContactLogin(id, data) {
    try {
      const token = await this.getAuthToken();
      const payload = { ...data, _method: 'PUT' };
      const query = data.crm_contact_id ? `?crm_contact_id=${data.crm_contact_id}` : '';
      let response;
      try {
        response = await fetch(`${this.baseURL}/api/crm/contact-login/${id}${query}`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload)
        });
      } catch (e) {
        response = null;
      }
      if (!response || !response.ok) {
        response = await fetch(`${this.baseURL}/crm/contact-login/${id}${query}`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload)
        });
      }
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update contact login: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) { /* console.error('Error updating contact login:', error); */ throw error; }
  }

  async deleteCrmContactLogin(id, crm_contact_id) {
    try {
      const token = await this.getAuthToken();
      const query = crm_contact_id ? `?crm_contact_id=${crm_contact_id}&_method=DELETE` : '?_method=DELETE';
      let response;
      try {
        response = await fetch(`${this.baseURL}/api/crm/contact-login/${id}${query}`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ _method: 'DELETE', crm_contact_id })
        });
      } catch (e) {
        response = null;
      }
      if (!response || !response.ok) {
        response = await fetch(`${this.baseURL}/crm/contact-login/${id}${query}`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ _method: 'DELETE', crm_contact_id })
        });
      }
      if (!response.ok) throw new Error(`Failed to delete contact login: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error deleting contact login:', error); */ throw error; }
  }

  async getCrmContactLoginForEdit(id, crm_contact_id) {
    try {
      const token = await this.getAuthToken();
      const query = crm_contact_id ? `?crm_contact_id=${crm_contact_id}&crud_type=edit` : '?crud_type=edit';
      let response;
      try {
        response = await fetch(`${this.baseURL}/api/crm/contact-login/${id}/edit${query}`, {
          method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
        });
      } catch (e) {
        response = null;
      }
      if (!response || !response.ok) {
        response = await fetch(`${this.baseURL}/crm/contact-login/${id}/edit${query}`, {
          method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
        });
      }
      if (!response.ok) throw new Error(`Failed to fetch contact login for edit: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching contact login for edit:', error); */ throw error; }
  }


  async getCrmContactLoginResources() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/api/crm/contact-login/create?crud_type=create`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) {
        response = await fetch(`${this.baseURL}/api/crm/all-contacts-login`, {
          method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
        });
      }
      if (!response.ok) throw new Error(`Failed to fetch contact login resources: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching contact login resources:', error); */ throw error; }
  }

  // CRM Call Logs API
  async getCrmCallLogs() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/call-log`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch call logs: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching call logs:', error); */ throw error; }
  }

  async saveCrmCallLogs(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/call-log`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to save call log: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error saving call log:', error); */ throw error; }
  }

  async massDeleteCrmCallLogs(selectedRows) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/mass-delete-call-log`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ selected_rows: selectedRows })
      });
      if (!response.ok) throw new Error(`Failed to delete call logs: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error deleting call logs:', error); */ throw error; }
  }

  async getCrmReports(type, params = {}) {
    try {
      const token = await this.getAuthToken();
      const query = new URLSearchParams();
      Object.keys(params || {}).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          query.append(key, params[key]);
        }
      });
      const queryString = query.toString() ? `?${query.toString()}` : '';

      let url = `${this.baseURL}/api/crm/reports${queryString}`;
      if (type === 'follow-ups-by-user') url = `${this.baseURL}/api/crm/follow-ups-by-user${queryString}`;
      if (type === 'follow-ups-by-contact') url = `${this.baseURL}/api/crm/follow-ups-by-contact${queryString}`;
      if (type === 'lead-to-customer') url = `${this.baseURL}/api/crm/lead-to-customer-report${queryString}`;
      let response;
      try {
        response = await fetch(url, {
          method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
        });
      } catch (e) {
        response = null;
      }
      if (!response || !response.ok) {
        let webUrl = `${this.baseURL}/crm/reports${queryString}`;
        if (type === 'follow-ups-by-user') webUrl = `${this.baseURL}/crm/follow-ups-by-user${queryString}`;
        if (type === 'follow-ups-by-contact') webUrl = `${this.baseURL}/crm/follow-ups-by-contact${queryString}`;
        if (type === 'lead-to-customer') webUrl = `${this.baseURL}/crm/lead-to-customer-report${queryString}`;
        response = await fetch(webUrl, {
          method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
        });
      }
      if (!response.ok) throw new Error(`Failed to fetch CRM reports: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching CRM reports:', error); */ throw error; }
  }

  // CRM Commissions API
  async getCrmCommissions() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/commissions`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch commissions: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching commissions:', error); */ throw error; }
  }

  // CRM Proposals API
  async getCrmProposalTemplates() {
    try {
      const token = await this.getAuthToken();
      let response;
      try {
        response = await fetch(`${this.baseURL}/api/crm/proposal-template`, {
          method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
        });
      } catch (e) { response = null; }
      if (!response || !response.ok) {
        response = await fetch(`${this.baseURL}/crm/proposal-template`, {
          method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
        });
      }
      if (!response.ok) throw new Error(`Failed to fetch proposal templates: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching proposal templates:', error); */ throw error; }
  }

  async getCrmProposals() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/proposals`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch proposals: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching proposals:', error); */ throw error; }
  }

  async createCrmProposalTemplate(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/proposal-template`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to create proposal template: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error creating proposal template:', error); */ throw error; }
  }

  async sendCrmProposal(data) {
    try {
      const token = await this.getAuthToken();
      let response;
      try {
        response = await fetch(`${this.baseURL}/api/crm/proposals`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(data)
        });
      } catch (e) { response = null; }
      if (!response || !response.ok) {
        response = await fetch(`${this.baseURL}/crm/proposals`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(data)
        });
      }
      if (!response || !response.ok) {
        response = await fetch(`${this.baseURL}/api/crm/send-proposal`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(data)
        });
      }
      if (!response.ok) throw new Error(`Failed to send proposal: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error sending proposal:', error); */ throw error; }
  }

  // CRM Order Requests API
  async getCrmOrderRequests(params = {}) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/order-request`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch order requests: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching order requests:', error); */ throw error; }
  }

  async createCrmOrderRequest(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/order-request`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to create order request: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error creating order request:', error); */ throw error; }
  }

  // CRM B2B Marketplace API
  async getCrmMarketplace() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/b2b-marketplace`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch B2B marketplace: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching B2B marketplace:', error); */ throw error; }
  }

  async saveCrmMarketplace(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/save-marketplace`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to save marketplace: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error saving marketplace:', error); */ throw error; }
  }

  // CRM Schedule Logs API
  async getCrmScheduleLogs(scheduleId) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/follow-up-log?schedule_id=${scheduleId}`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch schedule logs: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching schedule logs:', error); */ throw error; }
  }

  async createCrmScheduleLog(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/crm/follow-up-log`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to create schedule log: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error creating schedule log:', error); */ throw error; }
  }

  // ==================== Purchase Order SPA API ====================

  async getPurchaseOrders(params = {}) {
    try {
      const token = await this.getAuthToken();
      const query = new URLSearchParams(params).toString();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-orders?${query}`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch purchase orders: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching purchase orders:', error); */ throw error; }
  }

  async getPurchaseOrderCreateData() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-orders/create`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch purchase order create data: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching purchase order create data:', error); */ throw error; }
  }

  async createPurchaseOrder(data) {
    try {
      const token = await this.getAuthToken();
      const formData = this._objectToFormData(data);
      const response = await fetch(`${this.baseURL}/connector/api/purchase-orders`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }, body: formData
      });
      if (!response.ok) throw new Error(`Failed to create purchase order: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error creating purchase order:', error); */ throw error; }
  }

  async getPurchaseOrder(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-orders/${id}`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch purchase order: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching purchase order:', error); */ throw error; }
  }

  async getPurchaseOrderEditData(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-orders/${id}/edit`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch purchase order edit data: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching purchase order edit data:', error); */ throw error; }
  }

  async updatePurchaseOrder(id, data) {
    try {
      const token = await this.getAuthToken();
      const formData = this._objectToFormData(data);
      formData.append('_method', 'PUT');
      const response = await fetch(`${this.baseURL}/connector/api/purchase-orders/${id}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }, body: formData
      });
      if (!response.ok) throw new Error(`Failed to update purchase order: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error updating purchase order:', error); */ throw error; }
  }

  async deletePurchaseOrder(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-orders/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to delete purchase order: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error deleting purchase order:', error); */ throw error; }
  }

  async updatePurchaseOrderStatus(id, status) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-orders/${id}/status`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (!response.ok) throw new Error(`Failed to update purchase order status: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error updating purchase order status:', error); */ throw error; }
  }

  async updatePurchaseOrderShipping(id, formData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-orders/${id}/shipping`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: formData
      });
      if (!response.ok) throw new Error(`Failed to update purchase order shipping: ${response.status}`);
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async updateShipping(id, formData) {
    try {
      const token = await this.getAuthToken();
      if (!formData.has('id')) {
        formData.append('id', id);
      }
      const response = await fetch(`${this.baseURL}/connector/api/update-shipping-status`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: formData
      });
      if (!response.ok) throw new Error(`Failed to update shipping: ${response.status}`);
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async getUsers() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/user`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch users: ${response.status}`);
      const res = await response.json();
      return res.data || res || [];
    } catch (error) {
      return [];
    }
  }

  // ==================== Purchase Requisition SPA API ====================

  async getPurchaseRequisitions(params = {}) {
    try {
      const token = await this.getAuthToken();
      const query = new URLSearchParams(params).toString();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-requisitions?${query}`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch purchase requisitions: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching purchase requisitions:', error); */ throw error; }
  }

  async getPurchaseRequisitionCreateData() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-requisitions/create`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch purchase requisition create data: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching purchase requisition create data:', error); */ throw error; }
  }

  async createPurchaseRequisition(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-requisitions`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to create purchase requisition: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error creating purchase requisition:', error); */ throw error; }
  }

  async getPurchaseRequisition(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-requisitions/${id}`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch purchase requisition: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching purchase requisition:', error); */ throw error; }
  }

  async deletePurchaseRequisition(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-requisitions/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to delete purchase requisition: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error deleting purchase requisition:', error); */ throw error; }
  }

  async getPurchaseRequisitionProducts(params = {}) {
    try {
      const token = await this.getAuthToken();
      const query = new URLSearchParams(params).toString();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-requisitions/products?${query}`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch requisition products: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching requisition products:', error); */ throw error; }
  }

  // ==================== Purchase Return SPA API ====================

  async getPurchaseReturns(params = {}) {
    try {
      const token = await this.getAuthToken();
      const query = new URLSearchParams(params).toString();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-returns?${query}`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch purchase returns: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching purchase returns:', error); */ throw error; }
  }

  async getPurchaseReturn(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-returns/${id}`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch purchase return: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching purchase return:', error); */ throw error; }
  }

  async deletePurchaseReturn(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-returns/${id}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to delete purchase return: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error deleting purchase return:', error); */ throw error; }
  }

  async getCombinedPurchaseReturnCreateData() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/combined-purchase-returns/create`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch purchase return create data: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching purchase return create data:', error); */ throw error; }
  }

  async createCombinedPurchaseReturn(data) {
    try {
      const token = await this.getAuthToken();
      const formData = this._objectToFormData(data);
      const response = await fetch(`${this.baseURL}/connector/api/combined-purchase-returns`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }, body: formData
      });
      if (!response.ok) throw new Error(`Failed to create purchase return: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error creating purchase return:', error); */ throw error; }
  }

  async getPurchaseReturnShowData(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-returns/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      return await response.json();
    } catch (error) {
      console.error('Error fetching purchase return:', error);
      throw error;
    }
  }

  async getCombinedPurchaseReturnEditData(id) {
    try {
      const token = await this.getAuthToken();
      const cb = Date.now();
      const response = await fetch(`${this.baseURL}/connector/api/combined-purchase-returns/${id}/edit?_cb=${cb}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch purchase return edit data: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching purchase return edit data:', error); */ throw error; }
  }

  async updateCombinedPurchaseReturn(id, data) {
    try {
      const token = await this.getAuthToken();
      const formData = this._objectToFormData(data);
      formData.append('_method', 'PUT');
      const response = await fetch(`${this.baseURL}/connector/api/combined-purchase-returns/${id}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }, body: formData
      });
      if (!response.ok) throw new Error(`Failed to update purchase return: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error updating purchase return:', error); */ throw error; }
  }

  async getPurchaseReturnProductRow(params = {}) {
    try {
      const token = await this.getAuthToken();
      const query = new URLSearchParams(params).toString();
      const response = await fetch(`${this.baseURL}/connector/api/combined-purchase-returns/product-row?${query}`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`Failed to fetch purchase return product row: ${response.status}`);
      return await response.json();
    } catch (error) { /* console.error('Error fetching purchase return product row:', error); */ throw error; }
  }

  // Helper: convert plain object with nested arrays to FormData
  _objectToFormData(obj, formData = new FormData(), parentKey = '') {
    if (obj === null || obj === undefined) {
      return formData;
    }
    if (obj instanceof File || obj instanceof Blob) {
      formData.append(parentKey, obj);
      return formData;
    }
    if (Array.isArray(obj)) {
      obj.forEach((item, index) => {
        const key = parentKey ? `${parentKey}[${index}]` : `${index}`;
        this._objectToFormData(item, formData, key);
      });
      return formData;
    }
    if (typeof obj === 'object' && !(obj instanceof File)) {
      Object.keys(obj).forEach(key => {
        const newKey = parentKey ? `${parentKey}[${key}]` : key;
        this._objectToFormData(obj[key], formData, newKey);
      });
      return formData;
    }
    formData.append(parentKey, obj);
    return formData;
  }

  // Superadmin Impersonation: login as user
  async impersonateUser(userId) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/superadmin/impersonate/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Impersonation failed: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error impersonating user:', error); */
      throw error;
    }
  }

  async getSuperadminBusinessDetails(businessId) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/business/${businessId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch business details: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error getting superadmin business details:', error); */
      throw error;
    }
  }

  async getSuperadminBusinessUsers(businessId) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/users/${businessId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch business users: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error getting superadmin business users:', error); */
      throw error;
    }
  }

  async loginAsUser(userId) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/business/login-as-user/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Login as user failed: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error logging in as user:', error); */
      throw error;
    }
  }

  async updateSuperadminUserPassword(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/superadmin/business/update-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error(`Failed to update password: ${response.status}`);
      return await response.json();
    } catch (error) {
      /* console.error('Error updating password:', error); */
      throw error;
    }
  }

  // Essentials API Methods
  async safeParseJson(response, fallback = { data: [] }) {
    if (!response || !response.ok) return fallback;
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) return fallback;
    try {
      return await response.json();
    } catch (e) {
      return fallback;
    }
  }

  async getEssentialsTodos(params = {}) {
    try {
      const token = await this.getAuthToken();
      const query = new URLSearchParams(params).toString();
      let response = await fetch(`${this.baseURL}/connector/api/essentials/todo${query ? `?${query}` : ''}`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/essentials/todo${query ? `?${query}` : ''}`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { data: [] });
    } catch (error) { return { data: [] }; }
  }

  async createEssentialsTodo(data) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/essentials/todo`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/essentials/todo`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      }
      return await this.safeParseJson(response, { success: response.ok });
    } catch (error) { /* console.error('Error creating todo:', error); */ return { success: false }; }
  }

  async updateEssentialsTodo(id, data) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/essentials/todo/${id}`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/essentials/todo/${id}`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      }
      return await this.safeParseJson(response, { success: response.ok });
    } catch (error) { /* console.error('Error updating todo:', error); */ return { success: false }; }
  }

  async deleteEssentialsTodo(id) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/essentials/todo/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/essentials/todo/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { success: response.ok });
    } catch (error) { /* console.error('Error deleting todo:', error); */ return { success: false }; }
  }

  async getEssentialsDocuments() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/essentials/document`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/essentials/document`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { data: [] });
    } catch (error) { return { data: [] }; }
  }

  async uploadEssentialsDocument(formData) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/essentials/document`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }, body: formData });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/essentials/document`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }, body: formData });
      }
      return await this.safeParseJson(response, { success: response.ok });
    } catch (error) { /* console.error('Error uploading document:', error); */ return { success: false }; }
  }

  async deleteEssentialsDocument(id) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/essentials/document/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/essentials/document/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { success: response.ok });
    } catch (error) { /* console.error('Error deleting document:', error); */ return { success: false }; }
  }

  async getEssentialsReminders() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/essentials/reminder`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/essentials/reminder`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { data: [] });
    } catch (error) { return { data: [] }; }
  }

  async createEssentialsReminder(data) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/essentials/reminder`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/essentials/reminder`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      }
      return await this.safeParseJson(response, { success: response.ok });
    } catch (error) { /* console.error('Error creating reminder:', error); */ return { success: false }; }
  }

  async deleteEssentialsReminder(id) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/essentials/reminder/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/essentials/reminder/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { success: response.ok });
    } catch (error) { /* console.error('Error deleting reminder:', error); */ return { success: false }; }
  }

  async getEssentialsMessages() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/essentials/messages`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/essentials/messages`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { data: [] });
    } catch (error) { return { data: [] }; }
  }

  async sendEssentialsMessage(data) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/essentials/messages`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/essentials/messages`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      }
      return await this.safeParseJson(response, { success: response.ok });
    } catch (error) { /* console.error('Error sending message:', error); */ return { success: false }; }
  }

  async getEssentialsKB() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/essentials/knowledge-base`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/essentials/knowledge-base`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { data: [] });
    } catch (error) { return { data: [] }; }
  }

  async createEssentialsKB(data) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/essentials/knowledge-base`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/essentials/knowledge-base`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      }
      return await this.safeParseJson(response, { success: response.ok });
    } catch (error) { /* console.error('Error creating knowledge base article:', error); */ return { success: false }; }
  }

  async deleteEssentialsKB(id) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/essentials/knowledge-base/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/essentials/knowledge-base/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { success: response.ok });
    } catch (error) { /* console.error('Error deleting knowledge base article:', error); */ return { success: false }; }
  }

  async getEssentialsMemos() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/essentials/memos`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/essentials/memos`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { data: [] });
    } catch (error) { return { data: [] }; }
  }

  async createEssentialsMemo(data) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/essentials/memos`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/essentials/memos`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      }
      return await this.safeParseJson(response, { success: response.ok });
    } catch (error) { /* console.error('Error creating memo:', error); */ return { success: false }; }
  }

  async deleteEssentialsMemo(id) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/essentials/memos/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/essentials/memos/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { success: response.ok });
    } catch (error) { /* console.error('Error deleting memo:', error); */ return { success: false }; }
  }

  // Manufacturing API Methods
  async getManufacturingRecipes() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/manufacturing/get-recipes`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/manufacturing/get-recipes`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { data: [] });
    } catch (error) { /* console.error('Error fetching recipes:', error); */ return { data: [] }; }
  }

  async getManufacturingProductions(params = {}) {
    try {
      const token = await this.getAuthToken();
      const query = new URLSearchParams(params).toString();
      let response = await fetch(`${this.baseURL}/connector/api/manufacturing/get-productions?${query}`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/manufacturing/get-productions?${query}`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { data: [], pagination: {} });
    } catch (error) { /* console.error('Error fetching productions:', error); */ return { data: [], pagination: {} }; }
  }

  async getManufacturingSettings() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/manufacturing/get-settings`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/manufacturing/get-settings`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { settings: {} });
    } catch (error) { /* console.error('Error fetching mfg settings:', error); */ return { settings: {} }; }
  }

  async saveManufacturingSettings(data) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/manufacturing/save-settings`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/manufacturing/save-settings`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      }
      return await this.safeParseJson(response, { success: false });
    } catch (error) { /* console.error('Error saving mfg settings:', error); */ return { success: false }; }
  }

  async getManufacturingReport(params = {}) {
    try {
      const token = await this.getAuthToken();
      const query = new URLSearchParams(params).toString();
      let response = await fetch(`${this.baseURL}/connector/api/manufacturing/get-report?${query}`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/manufacturing/get-report?${query}`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { total_production: 0, total_production_cost: 0, total_sold: 0 });
    } catch (error) { /* console.error('Error fetching mfg report:', error); */ return { total_production: 0, total_production_cost: 0, total_sold: 0 }; }
  }

  async deleteManufacturingRecipe(id) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/manufacturing/delete-recipe/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/manufacturing/delete-recipe/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { success: false });
    } catch (error) { /* console.error('Error deleting recipe:', error); */ return { success: false }; }
  }

  async deleteManufacturingProduction(id) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/manufacturing/delete-production/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/manufacturing/delete-production/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { success: false });
    } catch (error) { /* console.error('Error deleting production:', error); */ return { success: false }; }
  }

  async updateManufacturingProductPrices(data) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/manufacturing/update-product-prices`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/manufacturing/update-product-prices`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      }
      return await this.safeParseJson(response, { success: false });
    } catch (error) { /* console.error('Error updating mfg prices:', error); */ return { success: false }; }
  }

  async createManufacturingProduction(data) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/manufacturing/create-production`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/manufacturing/create-production`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      }
      return await this.safeParseJson(response, { success: false, msg: 'Failed to create production' });
    } catch (error) { /* console.error('Error creating production:', error); */ return { success: false, msg: 'Error creating production' }; }
  }

  async createManufacturingRecipe(data) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/manufacturing/create-recipe`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/manufacturing/create-recipe`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      }
      return await this.safeParseJson(response, { success: false, msg: 'Failed to save recipe' });
    } catch (error) { /* console.error('Error creating recipe:', error); */ return { success: false, msg: 'Error creating recipe' }; }
  }

  async getManufacturingRecipeDetails(id) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/manufacturing/recipe-details/${id}`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/manufacturing/recipe-details/${id}`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, null);
    } catch (error) { /* console.error('Error fetching recipe details:', error); */ return null; }
  }

  async getManufacturingProductionDetails(id) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/manufacturing/production-details/${id}`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/manufacturing/production-details/${id}`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, null);
    } catch (error) { /* console.error('Error fetching production details:', error); */ return null; }
  }

  // WooCommerce Connector APIs
  async getWoocommerceIndex() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/woocommerce`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/woocommerce`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, null);
    } catch (error) { /* console.error('Error fetching WooCommerce index:', error); */ return null; }
  }

  async getWoocommerceApiSettings() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/woocommerce/api-settings`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/woocommerce/api-settings`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, null);
    } catch (error) { /* console.error('Error fetching WooCommerce API settings:', error); */ return null; }
  }

  async updateWoocommerceApiSettings(data) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/woocommerce/update-api-settings`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/woocommerce/update-api-settings`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      }
      return await this.safeParseJson(response, { success: false });
    } catch (error) { /* console.error('Error updating WooCommerce API settings:', error); */ return { success: false }; }
  }

  async syncWoocommerceCategories() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/woocommerce/sync-categories`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/woocommerce/sync-categories`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { success: false });
    } catch (error) { /* console.error('Error syncing WooCommerce categories:', error); */ return { success: false }; }
  }

  async syncWoocommerceProducts(type = 'all', limit = 100, offset = 0) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/woocommerce/sync-products?type=${type}&limit=${limit}&offset=${offset}`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/woocommerce/sync-products?type=${type}&limit=${limit}&offset=${offset}`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { success: false });
    } catch (error) { /* console.error('Error syncing WooCommerce products:', error); */ return { success: false }; }
  }

  async syncWoocommerceOrders() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/woocommerce/sync-orders`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/woocommerce/sync-orders`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { success: false });
    } catch (error) { /* console.error('Error syncing WooCommerce orders:', error); */ return { success: false }; }
  }

  async getWoocommerceSyncLogs() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/woocommerce/view-sync-log`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/woocommerce/view-sync-log`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { data: [] });
    } catch (error) { /* console.error('Error fetching WooCommerce sync logs:', error); */ return { data: [] }; }
  }

  async mapWoocommerceTaxRates(data) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/woocommerce/map-taxrates`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/woocommerce/map-taxrates`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(data) });
      }
      return await this.safeParseJson(response, { success: false });
    } catch (error) { /* console.error('Error mapping tax rates:', error); */ return { success: false }; }
  }

  async resetWoocommerceCategories() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/woocommerce/reset-categories`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/woocommerce/reset-categories`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { success: false });
    } catch (error) { /* console.error('Error resetting categories:', error); */ return { success: false }; }
  }

  async resetWoocommerceProducts() {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/woocommerce/reset-products`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      if (!response.ok && response.status === 404) {
        response = await fetch(`${this.baseURL}/woocommerce/reset-products`, { method: 'GET', headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } });
      }
      return await this.safeParseJson(response, { success: false });
    } catch (error) { /* console.error('Error resetting products:', error); */ return { success: false }; }
  }

  // Repair API Methods
  async getRepairDashboard() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/dashboard`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { job_sheets_by_status: [], job_sheets_by_service_staff: [], trending_brands: {}, trending_devices: {}, trending_device_models: {} };
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching repair dashboard:', error); */
      return { job_sheets_by_status: [], job_sheets_by_service_staff: [], trending_brands: {}, trending_devices: {}, trending_device_models: {} };
    }
  }

  async getRepairJobSheets(params = {}) {
    try {
      const token = await this.getAuthToken();
      const searchParams = new URLSearchParams(params);
      const response = await fetch(`${this.baseURL}/connector/api/repair/job-sheets?${searchParams.toString()}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { data: [] };
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching repair job sheets:', error); */
      return { data: [] };
    }
  }

  async getRepairRepairs(params = {}) {
    try {
      const token = await this.getAuthToken();
      const searchParams = new URLSearchParams(params);
      const response = await fetch(`${this.baseURL}/connector/api/repair/repairs?${searchParams.toString()}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { data: [] };
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching repair invoices:', error); */
      return { data: [] };
    }
  }

  async getRepairStatuses() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/statuses`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { data: [] };
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching repair statuses:', error); */
      return { data: [] };
    }
  }

  async getRepairDeviceModels() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/device-models`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { data: [] };
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching repair device models:', error); */
      return { data: [] };
    }
  }

  async getRepairBrands() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/brands`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { data: [] };
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching repair brands:', error); */
      return { data: [] };
    }
  }

  async getRepairDevices() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/devices`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { data: [] };
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching repair devices:', error); */
      return { data: [] };
    }
  }

  async getRepairSettings() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/settings`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return {};
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching repair settings:', error); */
      return {};
    }
  }

  async saveRepairSettings(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/settings`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      return await response.json();
    } catch (error) {
      /* console.error('Error saving repair settings:', error); */
      return { success: false };
    }
  }

  async createRepairJobSheet(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/job-sheets`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      return await response.json();
    } catch (error) {
      /* console.error('Error creating job sheet:', error); */
      return { success: false };
    }
  }

  async updateRepairJobSheet(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/job-sheets/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      return await response.json();
    } catch (error) {
      /* console.error('Error updating job sheet:', error); */
      return { success: false };
    }
  }

  async deleteRepairJobSheet(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/job-sheets/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting job sheet:', error); */
      return { success: false };
    }
  }

  async createRepairStatus(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/statuses`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      return await response.json();
    } catch (error) {
      /* console.error('Error creating repair status:', error); */
      return { success: false };
    }
  }

  async updateRepairStatus(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/statuses/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      return await response.json();
    } catch (error) {
      /* console.error('Error updating repair status:', error); */
      return { success: false };
    }
  }

  async deleteRepairStatus(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/statuses/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting repair status:', error); */
      return { success: false };
    }
  }

  // Project API Methods
  async getProjectProjects(params = {}) {
    try {
      const token = await this.getAuthToken();
      const searchParams = new URLSearchParams(params);
      const response = await fetch(`${this.baseURL}/connector/api/project/projects?${searchParams.toString()}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { data: [] };
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching projects:', error); */
      return { data: [] };
    }
  }

  async createProjectProject(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/projects`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const text = await response.text();
        let errMsg = 'Failed to create project (Server Error 500)';
        try { const errObj = JSON.parse(text); if (errObj.message) errMsg = errObj.message; else if (errObj.msg) errMsg = errObj.msg; } catch (e) { }
        return { success: false, msg: errMsg };
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error creating project:', error); */
      return { success: false, msg: error.message };
    }
  }

  async updateProjectProject(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/projects/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const text = await response.text();
        let errMsg = 'Failed to update project (Server Error 500)';
        try { const errObj = JSON.parse(text); if (errObj.message) errMsg = errObj.message; else if (errObj.msg) errMsg = errObj.msg; } catch (e) { }
        return { success: false, msg: errMsg };
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error updating project:', error); */
      return { success: false, msg: error.message };
    }
  }

  async deleteProjectProject(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/projects/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { success: false, msg: 'Server Error ' + response.status };
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting project:', error); */
      return { success: false, msg: error.message };
    }
  }

  async getProjectTasks(params = {}) {
    try {
      const token = await this.getAuthToken();
      const searchParams = new URLSearchParams(params);
      const response = await fetch(`${this.baseURL}/connector/api/project/tasks?${searchParams.toString()}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { data: [] };
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching project tasks:', error); */
      return { data: [] };
    }
  }

  async createProjectTask(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/tasks`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const text = await response.text();
        let errMsg = 'Failed to create task (Server Error 500)';
        try { const errObj = JSON.parse(text); if (errObj.message) errMsg = errObj.message; else if (errObj.msg) errMsg = errObj.msg; } catch (e) { }
        return { success: false, msg: errMsg };
      }
      return await response.json();
    } catch (error) {
      /* console.error('Error creating project task:', error); */
      return { success: false, msg: error.message };
    }
  }

  async updateProjectTask(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/tasks/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) return { success: false, msg: 'Server Error ' + response.status };
      return await response.json();
    } catch (error) {
      /* console.error('Error updating project task:', error); */
      return { success: false, msg: error.message };
    }
  }

  async deleteProjectTask(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/tasks/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { success: false, msg: 'Server Error ' + response.status };
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting project task:', error); */
      return { success: false, msg: error.message };
    }
  }

  async getProjectCategories() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/categories`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { data: [] };
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching project categories:', error); */
      return { data: [] };
    }
  }

  async createProjectCategory(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/categories`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) return { success: false, msg: 'Server Error ' + response.status };
      return await response.json();
    } catch (error) {
      /* console.error('Error creating project category:', error); */
      return { success: false, msg: error.message };
    }
  }

  async updateProjectCategory(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/categories/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) return { success: false, msg: 'Server Error ' + response.status };
      return await response.json();
    } catch (error) {
      /* console.error('Error updating project category:', error); */
      return { success: false, msg: error.message };
    }
  }

  async deleteProjectCategory(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/categories/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { success: false, msg: 'Server Error ' + response.status };
      return await response.json();
    } catch (error) {
      /* console.error('Error deleting project category:', error); */
      return { success: false, msg: error.message };
    }
  }

  async getProjectStatuses() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/statuses`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) {
        return { data: { not_started: 'Not Started', in_progress: 'In Progress', on_hold: 'On Hold', cancelled: 'Cancelled', completed: 'Completed' } };
      }
      return await response.json();
    } catch (error) {
      return { data: { not_started: 'Not Started', in_progress: 'In Progress', on_hold: 'On Hold', cancelled: 'Cancelled', completed: 'Completed' } };
    }
  }

  async getProjectUsers() {
    try {
      const res = await this.getUsers({ per_page: -1 });
      const userList = Array.isArray(res) ? res : (res?.data || []);
      const map = {};
      userList.forEach(u => {
        if (u && typeof u === 'object') {
          const name = u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username || `User #${u.id}`;
          map[u.id] = name;
        }
      });
      return { data: map };
    } catch (error) {
      return { data: {} };
    }
  }

  async getProjectCustomers() {
    try {
      const contactsRes = await this.getContacts({ type: 'customer' });
      const contactList = Array.isArray(contactsRes) ? contactsRes : (contactsRes?.data || []);
      const map = {};
      contactList.forEach(c => {
        if (c && typeof c === 'object') {
          map[c.id] = c.name || `Customer #${c.id}`;
        }
      });
      return { data: map };
    } catch (error) {
      return { data: {} };
    }
  }

  async getProjectSettings() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/settings`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { data: {} };
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching project settings:', error); */
      return { data: {} };
    }
  }

  async saveProjectSettings(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/project/settings`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      return await response.json();
    } catch (error) {
      /* console.error('Error saving project settings:', error); */
      return { success: false };
    }
  }

  async getProjectEmployeeTimeLogReport(params = {}) {
    try {
      const token = await this.getAuthToken();
      const searchParams = new URLSearchParams(params);
      let response = await fetch(`${this.baseURL}/connector/api/project/reports/employee-timelog?${searchParams.toString()}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { data: [] };
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching employee timelog report:', error); */
      return { data: [] };
    }
  }

  async getProjectProjectTimeLogReport(params = {}) {
    try {
      const token = await this.getAuthToken();
      const searchParams = new URLSearchParams(params);
      let response = await fetch(`${this.baseURL}/connector/api/project/reports/project-timelog?${searchParams.toString()}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { data: [] };
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching project timelog report:', error); */
      return { data: [] };
    }
  }

  // Repair Management APIs
  async getRepairDashboard() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/dashboard`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return {};
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching repair dashboard:', error); */
      return {};
    }
  }

  async getRepairJobSheets(params = {}) {
    try {
      const token = await this.getAuthToken();
      const searchParams = new URLSearchParams(params);
      const response = await fetch(`${this.baseURL}/connector/api/repair/job-sheets?${searchParams.toString()}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { data: [] };
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching job sheets:', error); */
      return { data: [] };
    }
  }

  async createRepairJobSheet(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/job-sheets`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const text = await response.text();
        let msg = 'Error creating Job Sheet';
        try { const errObj = JSON.parse(text); msg = errObj.msg || errObj.message || msg; } catch (e) { }
        return { success: false, msg };
      }
      return await response.json();
    } catch (error) {
      return { success: false, msg: error.message };
    }
  }

  async updateRepairJobSheet(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/job-sheets/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) return { success: false, msg: 'Server Error ' + response.status };
      return await response.json();
    } catch (error) {
      return { success: false, msg: error.message };
    }
  }

  async deleteRepairJobSheet(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/job-sheets/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { success: false, msg: 'Server Error ' + response.status };
      return await response.json();
    } catch (error) {
      return { success: false, msg: error.message };
    }
  }

  async getRepairRepairs(params = {}) {
    try {
      const token = await this.getAuthToken();
      const searchParams = new URLSearchParams(params);
      const response = await fetch(`${this.baseURL}/connector/api/repair/repairs?${searchParams.toString()}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { data: [] };
      return await response.json();
    } catch (error) {
      /* console.error('Error fetching repair invoices:', error); */
      return { data: [] };
    }
  }

  async getRepairStatuses() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/statuses`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { data: [] };
      return await response.json();
    } catch (error) {
      return { data: [] };
    }
  }

  async createRepairStatus(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/statuses`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) return { success: false, msg: 'Error creating status' };
      return await response.json();
    } catch (error) {
      return { success: false, msg: error.message };
    }
  }

  async updateRepairStatus(id, data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/statuses/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) return { success: false, msg: 'Error updating status' };
      return await response.json();
    } catch (error) {
      return { success: false, msg: error.message };
    }
  }

  async deleteRepairStatus(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/statuses/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { success: false, msg: 'Error deleting status' };
      return await response.json();
    } catch (error) {
      return { success: false, msg: error.message };
    }
  }

  async getRepairDeviceModels() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/device-models`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { data: [] };
      return await response.json();
    } catch (error) {
      return { data: [] };
    }
  }

  async getRepairBrands() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/brands`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { data: [] };
      return await response.json();
    } catch (error) {
      return { data: [] };
    }
  }

  async createRepairBrand(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/brands`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) return { success: false, msg: 'Error creating brand' };
      return await response.json();
    } catch (error) {
      return { success: false, msg: error.message };
    }
  }

  async getRepairDevices() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/devices`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return { data: [] };
      return await response.json();
    } catch (error) {
      return { data: [] };
    }
  }

  async getRepairSettings() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/settings`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) return {};
      return await response.json();
    } catch (error) {
      return {};
    }
  }

  async saveRepairSettings(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/settings`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) return { success: false, msg: 'Error saving settings' };
      return await response.json();
    } catch (error) {
      return { success: false, msg: error.message };
    }
  }

  async createRepairBrand(data) {
    try {
      const token = await this.getAuthToken();
      const payload = { name: data.name, description: data.description, use_for_repair: data.use_for_repair ? 1 : 0 };
      let response = await fetch(`${this.baseURL}/connector/api/brand`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (response.status === 405 || response.status === 404) {
        response = await fetch(`${this.baseURL}/connector/api/repair/brands`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(data)
        });
      }
      if (!response.ok) return { success: false, msg: 'Error creating brand' };
      return await response.json();
    } catch (error) {
      return { success: false, msg: error.message };
    }
  }

  async updateRepairBrand(id, data) {
    try {
      const token = await this.getAuthToken();
      const payload = { name: data.name, description: data.description, use_for_repair: data.use_for_repair ? 1 : 0 };
      let response = await fetch(`${this.baseURL}/connector/api/brand/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (response.status === 405 || response.status === 404) {
        response = await fetch(`${this.baseURL}/connector/api/repair/brands/${id}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(data)
        });
      }
      if (!response.ok) return { success: false, msg: 'Error updating brand' };
      return await response.json();
    } catch (error) {
      return { success: false, msg: error.message };
    }
  }

  async deleteRepairBrand(id) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/brand/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (response.status === 405 || response.status === 404) {
        response = await fetch(`${this.baseURL}/connector/api/repair/brands/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
        });
      }
      if (!response.ok) return { success: false, msg: 'Error deleting brand' };
      return await response.json();
    } catch (error) {
      return { success: false, msg: error.message };
    }
  }

  async deleteRepairDevice(id) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/repair/devices/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) return { success: false, msg: 'Error deleting device' };
      return await response.json();
    } catch (error) {
      return { success: false, msg: error.message };
    }
  }

  async deleteRepairDeviceModel(id) {
    try {
      const token = await this.getAuthToken();
      let response = await fetch(`${this.baseURL}/connector/api/repair/device-models/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' }
      });
      if (!response.ok) return { success: false, msg: 'Error deleting device model' };
      return await response.json();
    } catch (error) {
      return { success: false, msg: error.message };
    }
  }

  async createRepairDevice(data) {
    try {
      const token = await this.getAuthToken();
      const payload = { name: data.name, short_code: data.short_code, category_type: 'device' };
      let response = await fetch(`${this.baseURL}/connector/api/taxonomy`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (response.status === 405 || response.status === 404) {
        response = await fetch(`${this.baseURL}/connector/api/repair/devices`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(data)
        });
      }
      if (!response.ok) return { success: false, msg: 'Error creating device' };
      return await response.json();
    } catch (error) {
      return { success: false, msg: error.message };
    }
  }

  async createRepairDeviceModel(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/repair/device-models`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.ok) return { success: false, msg: 'Error creating device model' };
      return await response.json();
    } catch (error) {
      return { success: false, msg: error.message };
    }
  }

  async createRepairCustomer(data) {
    try {
      const token = await this.getAuthToken();
      const payload = { name: data.name, mobile: data.mobile, email: data.email, type: 'customer' };
      let response = await fetch(`${this.baseURL}/connector/api/contactapi`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (response.status === 405 || response.status === 404) {
        response = await fetch(`${this.baseURL}/connector/api/repair/customers`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(data)
        });
      }
      if (!response.ok) return { success: false, msg: 'Error creating customer' };
      return await response.json();
    } catch (error) {
      return { success: false, msg: error.message };
    }
  }

  async getExpenses(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '' && params[key] !== 'all') {
          queryParams.append(key, params[key]);
        }
      });
      const queryString = queryParams.toString();
      const url = `${this.baseURL}/connector/api/expense${queryString ? '?' + queryString : ''}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch expenses');
      return await response.json();
    } catch (error) {
      /* console.error('getExpenses error:', error); */
      throw error;
    }
  }

  async getExpenseCategories() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/expense-categories`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) return { data: [] };
      return await response.json();
    } catch (error) {
      /* console.error('getExpenseCategories error:', error); */
      return { data: [] };
    }
  }

  async deleteExpense(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/expense/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to delete expense');
      return await response.json();
    } catch (error) {
      /* console.error('deleteExpense error:', error); */
      throw error;
    }
  }

  async getCustomerSupplierReport(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '' && params[key] !== 'all') {
          queryParams.append(key, params[key]);
        }
      });
      const queryString = queryParams.toString();
      const url = `${this.baseURL}/connector/api/reports/customer-supplier${queryString ? '?' + queryString : ''}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch customer supplier report');
      return await response.json();
    } catch (error) {
      /* console.error('getCustomerSupplierReport error:', error); */
      throw error;
    }
  }

  async getCustomerSupplierReportFilterOptions() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/reports/customer-supplier/filter-options`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      return await response.json();
    } catch (error) {
      /* console.error('getCustomerSupplierReportFilterOptions error:', error); */
      return { data: {} };
    }
  }

  async getCustomerGroupReport(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '' && params[key] !== 'all') {
          queryParams.append(key, params[key]);
        }
      });
      const queryString = queryParams.toString();
      const url = `${this.baseURL}/connector/api/reports/customer-groups${queryString ? '?' + queryString : ''}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch customer group report');
      return await response.json();
    } catch (error) {
      /* console.error('getCustomerGroupReport error:', error); */
      throw error;
    }
  }

  async getCustomerGroupReportFilterOptions() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/reports/customer-groups/filter-options`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) return { data: {} };
      return await response.json();
    } catch (error) {
      /* console.error('getCustomerGroupReportFilterOptions error:', error); */
      return { data: {} };
    }
  }

  // --- Purchase Invoice AI Endpoints ---

  async uploadPurchaseInvoice(file) {
    try {
      const token = await this.getAuthToken();
      const formData = new FormData();
      formData.append('invoice_file', file);

      const response = await fetch(`${this.baseURL}/connector/api/purchase/upload-invoice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      if (!response.ok) throw new Error('Upload failed');
      return await response.json();
    } catch (error) {
      /* console.error('Error in uploadPurchaseInvoice:', error); */
      throw error;
    }
  }

  async processPurchaseInvoice(uploadId) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchase/process-invoice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ upload_id: uploadId })
      });
      if (!response.ok) throw new Error('Processing failed');
      return await response.json();
    } catch (error) {
      /* console.error('Error in processPurchaseInvoice:', error); */
      throw error;
    }
  }

  async mapPurchaseInvoiceItem(uploadId, itemId, productId, variationId) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchase/invoice/${uploadId}/map-item`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          item_id: itemId,
          product_id: productId,
          variation_id: variationId
        })
      });
      if (!response.ok) throw new Error('Mapping failed');
      return await response.json();
    } catch (error) {
      /* console.error('Error in mapPurchaseInvoiceItem:', error); */
      throw error;
    }
  }

  async downloadPurchaseOrderPdf(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/download-purchase-order/${id}/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/pdf'
        }
      });
      if (!response.ok) throw new Error('Failed to download PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      return true;
    } catch (error) {
      console.error('Error downloading PO PDF:', error);
      throw error;
    }
  }

  async sendNotification(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/notifications/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to send notification');
      return await response.json();
    } catch (error) {
      console.error('Error sending notification:', error);
      throw error;
    }
  }

  async getPurchaseOrderCreateData(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-orders/create?${queryString}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch purchase order create data');
      return await response.json();
    } catch (error) {
      console.error('Error fetching purchase order create data:', error);
      throw error;
    }
  }

  async getPurchaseOrderEditData(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-orders/${id}/edit`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch purchase order edit data');
      return await response.json();
    } catch (error) {
      console.error('Error fetching purchase order edit data:', error);
      throw error;
    }
  }

  async getNotificationTemplate(id, templateFor = 'purchase_order') {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/notifications/template/${id}?template_for=${templateFor}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch notification template');
      return await response.json();
    } catch (error) {
      console.error('Error fetching notification template:', error);
      throw error;
    }
  }

  async getPurchaseRequisitionCreateData() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-requisitions/create`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch requisition create data');
      return await response.json();
    } catch (error) {
      console.error('Error fetching requisition create data:', error);
      throw error;
    }
  }

  async getPurchaseRequisitionProducts(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-requisitions/products?${queryString}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch requisition products');
      const data = await response.json();
      return data.data || data;
    } catch (error) {
      console.error('Error fetching requisition products:', error);
      throw error;
    }
  }

  async createPurchaseRequisition(data) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-requisitions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) throw new Error('Failed to create purchase requisition');
      return await response.json();
    } catch (error) {
      console.error('Error creating purchase requisition:', error);
      throw error;
    }
  }

  async getPurchaseRequisitions(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryString = new URLSearchParams(params).toString();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-requisitions?${queryString}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch purchase requisitions');
      return await response.json();
    } catch (error) {
      console.error('Error fetching purchase requisitions:', error);
      throw error;
    }
  }

  async deletePurchaseRequisition(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchase-requisitions/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to delete purchase requisition');
      return await response.json();
    } catch (error) {
      console.error('Error deleting purchase requisition:', error);
      throw error;
    }
  }

  // MSA Tobacco Report APIs
  async getMsaTobaccoData(params = {}) {
    try {
      const query = new URLSearchParams(params).toString();
      const endpoint = `/connector/api/reports/msa-tobacco${query ? `?${query}` : ''}`;
      const res = await this.fetchWithAuth(endpoint);
      return res;
    } catch (error) {
      console.error('Error fetching MSA Tobacco Data:', error);
      throw error;
    }
  }

  async getMsaTobaccoHistory() {
    try {
      const res = await this.fetchWithAuth('/connector/api/reports/msa-tobacco/history');
      return res;
    } catch (error) {
      console.error('Error fetching MSA Report History:', error);
      throw error;
    }
  }

  // Sales Order APIs
  async getSalesOrders(params = {}) {
    try {
      const cleanParams = {};
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          cleanParams[key] = params[key];
        }
      });
      const query = new URLSearchParams(cleanParams).toString();
      const endpoint = `/connector/api/sales-orders${query ? `?${query}` : ''}`;
      const res = await this.fetchWithAuth(endpoint);
      return res;
    } catch (error) {
      console.error('Error fetching sales orders:', error);
      throw error;
    }
  }
}

// Create singleton instance
const apiService = new ApiService();

export default apiService;

